import { MobXProviderContext } from "mobx-react";
import { AccordionTab } from "primereact/accordion";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RouterAddress } from "../../../constants/RouterAddress";
import { Elements } from '@stripe/react-stripe-js';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import CheckoutForm from "../components/CheckoutForm";
import {
  BillingConstants,
  DEFAULT_BILLING_BRAND_CONFIG,
  SHARED_BUNDLE_SORT_PRIORITY,
  SHARED_ONE_TIME_BUNDLE_DISPLAY_PRIORITY,
  SHARED_ONE_TIME_BUNDLE_PACKAGES,
  SHARED_ONE_TIME_TAB_CONFIG_BY_ROLE,
  getSharedPlanComparisonConfigByRole,
  getSharedSubscriptionPlanContentByType,
  SHARED_ROLE_ONE_TIME_BUNDLE_PURCHASE_TYPES,
} from "../../../constants/billing";
import { Divider } from "primereact/divider";
import { ProgressSpinner } from "primereact/progressspinner";
import { ROLE_TYPE_ADMIN, ROLE_TYPE_BUSINESS_VENDOR, ROLE_TYPE_CAREGIVER, ROLE_TYPE_FACILITY_WORKER, ROLE_TYPE_FAMILY_MEMBER, ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { confirmDialog } from "primereact/confirmdialog";
import { Tag } from "primereact/tag";
import { Steps } from "primereact/steps";
import { UserProfile } from "../../../models/userProfile";
import {
  appDialogContentStyle,
  appDialogFooterStyle,
  appDialogHeaderStyle,
} from "../../../components/general/uiSurfaceStyles";
import {
  BillingAddressDialog,
  BillingOneTimeTabContent,
  BillingPaymentHistoryDialog,
  BillingPaymentDetailsDialog,
  BillingOneTimeCheckoutDialog,
  BillingPlanComparisonDialog,
  BillingSubscriptionTabContent,
} from "./BillingPageSections";
import {
  ADMIN_TEST_FEATURE_CREDIT_OPTION,
  BILLING_ADDRESS_TAX_VERIFICATION_AMOUNT,
  EMPTY_BILLING_ADDRESS,
  FEATURE_CREDIT_PURCHASE_OPTIONS,
  INITIAL_PLAN_QUANTITIES,
  getBillingDetailsFeatureCreditBalances,
  getBillingTabIndexFromSearch,
  getFallbackFeatureCreditPurchaseOptions,
  getSubscriptionCardContent,
  getSubscriptionDisplayMeta,
  hasCompleteBillingAddress,
  isBundlePurchaseType,
  normalizeBillingAddress,
  stripePromise,
  usesSingleAddSubscriptionControl,
} from "./billingPageShared";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";


const CANCELLATION_REASON_OPTIONS = [
  { label: "Too expensive", value: "Too expensive" },
  { label: "Not using it enough", value: "Not using it enough" },
  { label: "Missing features I need", value: "Missing features I need" },
  { label: "Too many manual steps", value: "Too many manual steps" },
  { label: "Technical issues or bugs", value: "Technical issues or bugs" },
  { label: "Switching roles or workflow", value: "Switching roles or workflow" },
  { label: "Switching to another solution", value: "Switching to another solution" },
  { label: "Billing or payment concerns", value: "Billing or payment concerns" },
  { label: "No longer need the service", value: "No longer need the service" },
  { label: "Temporary pause in business needs", value: "Temporary pause in business needs" },
  { label: "Other", value: "Other" },
];
const PROVIDER_NURSE_ADDON_SUBSCRIPTION_PRICE = 5;
const PROVIDER_SUBSCRIPTION_UPGRADE_PURCHASE_TYPE = "provider_subscription_upgrade";
const SUBSCRIPTION_PLAN_UPDATE_PURCHASE_TYPE = "subscription_plan_update";

const BillingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const authExperience = useAuthExperience();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [subscriptionWizardStep, setSubscriptionWizardStep] = useState<number>(0);
  const [subscriptionWizardDialogVisible, setSubscriptionWizardDialogVisible] = useState<boolean>(false);
  const [updateSubscriptionFlag, setUpdateSubscriptionFlag] = useState<boolean>(false);
  const [userId, setUserId] = useState<string>(authStore.getUserId());
  const [selectedActorRole, setSelectedActorRole] = useState<string>("");
  const [isUserSubscribed, setIsUserSubscribed] = useState<boolean>(true);
  const [allowedHomes, setAllowedHomes] = useState<number>(0);
  const [purchasedAllowedHomes, setPurchasedAllowedHomes] = useState<number>(0);
  const [allowedResidents, setAllowedResidents] = useState<number>(0);
  const [purchasedAllowedResidents, setPurchasedAllowedResidents] = useState<number>(0);
  const [allowedCaregivers, setAllowedCaregivers] = useState<number>(0);
  const [purchasedAllowedCaregivers, setPurchasedAllowedCaregivers] = useState<number>(0);
  const [remainingHomes, setRemainingHomes] = useState<number>(0);
  const [remainingResidents, setRemainingResidents] = useState<number>(0);
  const [currentHomeCount, setCurrentHomeCount] = useState<number>(0);
  const [currentResidentCount, setCurrentResidentCount] = useState<number>(0);
  const [websitePublishCreditsAvailable, setWebsitePublishCreditsAvailable] = useState<number>(0);
  const [websitePublishUnlockedHomeIds, setWebsitePublishUnlockedHomeIds] = useState<string[]>([]);
  const [setUpIntentClientSecret, setSetUpIntentClientSecret] = useState<string>("");
  const [paymentIntentType, setPaymentIntentType] = useState<string>("");

  const [loadingPaymentIntent, setLoadingPaymentIntent] = useState<boolean>(false);
  const [planQuantities, setPlanQuantities] = useState<Record<string, number>>(INITIAL_PLAN_QUANTITIES);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [trialEndDate, setTrialEndDate] = useState<Date | null>(null);
  const [currentBillingPeriodStart, setCurrentBillingPeriodStart] = useState<Date | null>(null);
  const [currentBillingPeriodEnd, setCurrentBillingPeriodEnd] = useState<Date | null>(null);
  const [subscriptionCancelDate, setSubscriptionCancelDate] = useState<Date | null>(null);
  const [subscriptionPrice, setSubscriptionPrice] = useState<number>(0);
  const [currentNursePlanTier, setCurrentNursePlanTier] = useState<string>("");
  const [adminTestSubscriptions, setAdminTestSubscriptions] = useState<number>(0);
  const [taxDetails, setTaxDetails] = useState<any>(null);
  const [paymentMethodSummary, setPaymentMethodSummary] = useState<any>(null);
  const [invoiceHistory, setInvoiceHistory] = useState<any[]>([]);
  const [featureCreditPurchaseHistory, setFeatureCreditPurchaseHistory] = useState<any[]>([]);
  const [nextInvoiceCursor, setNextInvoiceCursor] = useState<string | null>(null);
  const [nextFeatureCursor, setNextFeatureCursor] = useState<string | null>(null);
  const [loadingMoreInvoices, setLoadingMoreInvoices] = useState<boolean>(false);
  const [loadingMoreFeatureHistory, setLoadingMoreFeatureHistory] = useState<boolean>(false);
  const [billingRecoveryStatus, setBillingRecoveryStatus] = useState<string>("healthy");
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>("");
  const [lastPaymentFailureReason, setLastPaymentFailureReason] = useState<string>("");
  const [lastPaymentFailureAt, setLastPaymentFailureAt] = useState<Date | null>(null);
  const [nextRetryAt, setNextRetryAt] = useState<Date | null>(null);
  const [canRetryPayment, setCanRetryPayment] = useState<boolean>(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState<boolean>(false);
  const [featureCreditBalances, setFeatureCreditBalances] = useState<Record<string, number>>({});
  const [currentBalanceItems, setCurrentBalanceItems] = useState<Array<{ key: string; label: string; value: number }>>([]);
  const [billingCatalogByRole, setBillingCatalogByRole] = useState<Record<string, any>>({});
  const [selectedBundlePurchaseType, setSelectedBundlePurchaseType] = useState<string>("");
  const [bundleQuantities, setBundleQuantities] = useState<Record<string, number>>(
    Object.fromEntries(SHARED_ONE_TIME_BUNDLE_PACKAGES.map((bundle) => [bundle.purchaseType, 1]))
  );
  const [paymentMode, setPaymentMode] = useState<"subscription" | "bundle" | "cardUpdate">("subscription");
  const [paymentReturnMessage, setPaymentReturnMessage] = useState<string>("");
  const [processingReturn, setProcessingReturn] = useState<boolean>(false);
  const [billingTabIndex, setBillingTabIndex] = useState<number>(0);
  const [subscriptionCheckoutKind, setSubscriptionCheckoutKind] = useState<"standard" | "providerUpgrade" | "subscriptionUpdateCharge">("standard");
  const [bundleTaxDetails, setBundleTaxDetails] = useState<any>(null);
  const [billingAddress, setBillingAddress] = useState<any>(EMPTY_BILLING_ADDRESS);
  const [billingAddressDraft, setBillingAddressDraft] = useState<any>(EMPTY_BILLING_ADDRESS);
  const [billingAddressErrors, setBillingAddressErrors] = useState<Record<string, string>>({});
  const [isBillingAddressElementComplete, setIsBillingAddressElementComplete] = useState<boolean>(false);
  const [billingAddressDialogVisible, setBillingAddressDialogVisible] = useState<boolean>(false);
  const [billingAddressSaving, setBillingAddressSaving] = useState<boolean>(false);
  const [userProfileAddressSeed, setUserProfileAddressSeed] = useState<any>(EMPTY_BILLING_ADDRESS);
  const [currentUserProfile, setCurrentUserProfile] = useState<UserProfile | null>(null);
  const [cancelDialogVisible, setCancelDialogVisible] = useState<boolean>(false);
  const [cancelDialogMode, setCancelDialogMode] = useState<"end_of_period">("end_of_period");
  const [cancelDialogReason, setCancelDialogReason] = useState<string>("");
  const [cancelDialogOtherReason, setCancelDialogOtherReason] = useState<string>("");
  const [cancelDialogError, setCancelDialogError] = useState<string>("");

  const paymentRef = useRef<HTMLDivElement>(null);
  const paymentIntentRef = useRef<any>(null);
  const billingToolsMenuRef = useRef<HTMLDivElement>(null);
  const pendingBillingAddressActionRef = useRef<null | ((resolvedBillingAddress: any) => Promise<void> | void)>(null);
  const [paymentHistoryVisible, setPaymentHistoryVisible] = useState<boolean>(false);
  const [paymentDetailsVisible, setPaymentDetailsVisible] = useState<boolean>(false);
  const [billingToolsVisible, setBillingToolsVisible] = useState<boolean>(false);
  const [oneTimeBalancesExpanded, setOneTimeBalancesExpanded] = useState<boolean>(false);
  const [oneTimeCheckoutVisible, setOneTimeCheckoutVisible] = useState<boolean>(false);
  const [planComparisonVisible, setPlanComparisonVisible] = useState<boolean>(false);
  const [showProviderPlanIncludes, setShowProviderPlanIncludes] = useState<boolean>(true);
  const [showNursePlanIncludes, setShowNursePlanIncludes] = useState<boolean>(true);
  const userRoleType = authStore.getUserRoleType();
  const billingBrandConfig = useMemo(() => ({
    ...DEFAULT_BILLING_BRAND_CONFIG,
    brandKey: authExperience.brandKey,
    brandName: authExperience.brandName,
    providerLabel: authExperience.nurseWorkspace.providerLabel,
    providerPluralLabel: authExperience.nurseWorkspace.providerPluralLabel,
    websiteLabel: authExperience.discovery.jobWebsiteLabel,
    vendorCommunityLabel: authExperience.vocabulary.facilityPluralLower,
  }), [
    authExperience.brandKey,
    authExperience.brandName,
    authExperience.discovery.jobWebsiteLabel,
    authExperience.nurseWorkspace.providerLabel,
    authExperience.nurseWorkspace.providerPluralLabel,
    authExperience.vocabulary.facilityPluralLower,
  ]);
  const roleTypeSubscriptionMapping = useMemo(
    () => BillingConstants.getRoleTypeSubscriptionMapping(billingBrandConfig),
    [billingBrandConfig]
  );
  const sharedPlanComparisonConfigByRole = useMemo(
    () => getSharedPlanComparisonConfigByRole(billingBrandConfig),
    [billingBrandConfig]
  );
  const sharedSubscriptionPlanContentByType = useMemo(
    () => getSharedSubscriptionPlanContentByType(billingBrandConfig),
    [billingBrandConfig]
  );
  const providerSingleHomeSubscriptionPrice = useMemo(
    () => BillingConstants.getProviderSingleHomeSubscriptionPrice(billingBrandConfig),
    [billingBrandConfig]
  );
  const providerAddOnConfig = useMemo(
    () => BillingConstants.getProviderAddOnConfig(billingBrandConfig),
    [billingBrandConfig]
  );
  const isAdminBillingUser = userRoleType === ROLE_TYPE_ADMIN;
  const activeBillingRoleType = isAdminBillingUser
    ? (selectedActorRole || "admin")
    : (userRoleType || ROLE_TYPE_PROVIDER);
  const activeBillingCatalog = billingCatalogByRole[activeBillingRoleType] || null;
  const isFacilityWorkerBillingRole = activeBillingRoleType === ROLE_TYPE_FACILITY_WORKER;
  const facilityWorkerBillingService = BillingConstants.FACILITY_WORKER_FREE_SERVICE;
  const isNurseBillingRole = !isAdminBillingUser && (activeBillingRoleType === ROLE_TYPE_NURSE || activeBillingRoleType === ROLE_TYPE_MHP);
  const isNurseStyleRoleType = (roleType: string) => roleType === ROLE_TYPE_NURSE || roleType === ROLE_TYPE_MHP;
  const isFixedPriceNoBundleBillingRole = !isAdminBillingUser
    && (
      activeBillingRoleType === ROLE_TYPE_CAREGIVER
      || activeBillingRoleType === ROLE_TYPE_FAMILY_MEMBER
      || activeBillingRoleType === ROLE_TYPE_BUSINESS_VENDOR
    );
  const selectedNurseResidentCount = Number(planQuantities[BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN] || 0);
  const hasCurrentNursePlan = isUserSubscribed && !!currentNursePlanTier;
  const isReplacementPriceRole = isNurseBillingRole || isFixedPriceNoBundleBillingRole;
  const visibleFeatureCreditPurchaseOptions = useMemo(() => (
    activeBillingCatalog?.featureCredits
      ? activeBillingCatalog.featureCredits
      : getFallbackFeatureCreditPurchaseOptions(isAdminBillingUser, isNurseBillingRole, isFixedPriceNoBundleBillingRole)
  ), [activeBillingCatalog, isAdminBillingUser, isFixedPriceNoBundleBillingRole, isNurseBillingRole]);
  const sharedOneTimeBundleMap = useMemo(
    () => new Map(SHARED_ONE_TIME_BUNDLE_PACKAGES.map((bundle) => [bundle.purchaseType, bundle])),
    []
  );
  const visibleOneTimeBundlePackages = useMemo(() => (
    (
      activeBillingCatalog?.oneTimeBundles
        ? activeBillingCatalog.oneTimeBundles
        : SHARED_ONE_TIME_BUNDLE_PACKAGES.filter((bundle) =>
          (
            SHARED_ROLE_ONE_TIME_BUNDLE_PURCHASE_TYPES[activeBillingRoleType]
            || SHARED_ROLE_ONE_TIME_BUNDLE_PURCHASE_TYPES[userRoleType]
            || []
          ).includes(bundle.purchaseType)
        )
    ).map((bundle: any) => ({
      ...bundle,
      ...(sharedOneTimeBundleMap.get(bundle.purchaseType) || {}),
    }))
  ), [activeBillingCatalog, activeBillingRoleType, userRoleType, sharedOneTimeBundleMap]);
  const oneTimeTabConfig = activeBillingCatalog?.oneTimeTab
    || SHARED_ONE_TIME_TAB_CONFIG_BY_ROLE[activeBillingRoleType]
    || SHARED_ONE_TIME_TAB_CONFIG_BY_ROLE[userRoleType]
    || SHARED_ONE_TIME_TAB_CONFIG_BY_ROLE[ROLE_TYPE_FAMILY_MEMBER];
  const filteredOneTimeBundlePackages = visibleOneTimeBundlePackages
    .sort((firstBundle: any, secondBundle: any) => {
      const explicitOrderDifference =
        SHARED_ONE_TIME_BUNDLE_DISPLAY_PRIORITY.indexOf(firstBundle.purchaseType as any)
        - SHARED_ONE_TIME_BUNDLE_DISPLAY_PRIORITY.indexOf(secondBundle.purchaseType as any);
      if (explicitOrderDifference !== 0) {
        return explicitOrderDifference;
      }

      const getBundleCategoryRank = (bundle: any) => {
        const bundleCategories = bundle.categories || [];
        const matchedPriority = SHARED_BUNDLE_SORT_PRIORITY.findIndex((category) => bundleCategories.includes(category));
        return matchedPriority === -1 ? Number.MAX_SAFE_INTEGER : matchedPriority;
      };

      const categoryRankDifference = getBundleCategoryRank(firstBundle) - getBundleCategoryRank(secondBundle);
      if (categoryRankDifference !== 0) {
        return categoryRankDifference;
      }

      return Number(firstBundle.price || 0) - Number(secondBundle.price || 0);
    });

  useEffect(() => {
    if (selectedBundlePurchaseType && !visibleOneTimeBundlePackages.some((bundle) => bundle.purchaseType === selectedBundlePurchaseType)) {
      setSelectedBundlePurchaseType("");
    }
  }, [selectedBundlePurchaseType, visibleOneTimeBundlePackages]);

  const getFeatureCreditsFromBalances = (featureType: string, balances?: Record<string, number> | null) =>
    Number(balances?.[featureType] || 0);

  const getFeatureCreditOption = (featureType: string) => {
    const baseOption = visibleFeatureCreditPurchaseOptions.find((item) => item.featureType === featureType)
      || FEATURE_CREDIT_PURCHASE_OPTIONS.find((item) => item.featureType === featureType)
      || visibleFeatureCreditPurchaseOptions[0]
      || FEATURE_CREDIT_PURCHASE_OPTIONS[0]
      || ADMIN_TEST_FEATURE_CREDIT_OPTION;
    if (!baseOption) {
      return undefined as any;
    }
    return baseOption;
  };

  const getBundleOption = (purchaseType: string) =>
    visibleOneTimeBundlePackages.find((item) => item.purchaseType === purchaseType)
    || SHARED_ONE_TIME_BUNDLE_PACKAGES.find((item) => item.purchaseType === purchaseType);

  const getInitialBillingAddressDraft = () => {
    if (hasCompleteBillingAddress(billingAddress)) {
      return normalizeBillingAddress(billingAddress);
    }

    if (hasCompleteBillingAddress(userProfileAddressSeed)) {
      return normalizeBillingAddress(userProfileAddressSeed);
    }

    return { ...EMPTY_BILLING_ADDRESS };
  };

  const validateBillingAddressDraft = (addressDraft: any) => {
    const normalized = normalizeBillingAddress(addressDraft);
    const nextErrors: Record<string, string> = {};

    if (!isBillingAddressElementComplete) {
      nextErrors.addressElement = "Please complete the Stripe billing address form.";
    }

    const normalizedState = String(normalized.state || "").trim().toUpperCase();
    const normalizedCountry = String(normalized.country || "").trim().toUpperCase();
    if ((normalizedState && normalizedState !== "WA" && normalizedState !== "WASHINGTON")
      || (normalizedCountry && normalizedCountry !== "US" && normalizedCountry !== "USA" && normalizedCountry !== "UNITED STATES")) {
      nextErrors.addressElement = `Please use a ${"Washington, US"} billing address.`;
    }

    return { normalized, nextErrors };
  };

  const getBillingFieldError = (field: string) => billingAddressErrors[field] || "";

  const openBillingAddressDialog = (resumeAction?: (resolvedBillingAddress: any) => Promise<void> | void) => {
    pendingBillingAddressActionRef.current = resumeAction || null;
    const nextDraft = getInitialBillingAddressDraft();
    setBillingAddressDraft(nextDraft);
    setIsBillingAddressElementComplete(hasCompleteBillingAddress(nextDraft));
    setBillingAddressErrors({});
    setBillingAddressDialogVisible(true);
  };

  const fetchTaxDetailsForAmount = async (amount: number, providedBillingAddress?: any) => {
    try {
      const result = await backendAPIStore.getTaxDetails(amount, providedBillingAddress);
      const nextBillingAddress = result?.billingAddress ? normalizeBillingAddress(result.billingAddress) : null;

      if (nextBillingAddress && hasCompleteBillingAddress(nextBillingAddress)) {
        setBillingAddress(nextBillingAddress);
      }

      return result;
    } catch (error: any) {
      throw new Error(error?.message || "Please provide a valid address.");
    }
  };

  const verifyBillingAddressTaxResponse = async (normalizedBillingAddress: any) => {
    const verificationTaxDetails = await fetchTaxDetailsForAmount(
      BILLING_ADDRESS_TAX_VERIFICATION_AMOUNT,
      normalizedBillingAddress
    );
    const verificationTaxAmount = Number(verificationTaxDetails?.tax || 0);

    if (!verificationTaxDetails || verificationTaxAmount <= 0) {
      throw new Error("We couldn't verify tax for this billing address. Please confirm the address and try again.");
    }

    return verificationTaxDetails;
  };

  const syncBillingAddressToUserProfile = async (normalizedBillingAddress: any) => {
    if (!currentUserProfile || !userId) {
      return;
    }

    const updatedUserProfile = await backendAPIStore.updateUserProfile({
      ...currentUserProfile,
      userId,
      addressLine1: normalizedBillingAddress.line1 || "",
      addressLine2: normalizedBillingAddress.line2 || "",
      city: normalizedBillingAddress.city || "",
      state: normalizedBillingAddress.state || "",
      country: normalizedBillingAddress.country || "",
      zipcode: normalizedBillingAddress.postal_code || "",
      formattedAddress: [
        normalizedBillingAddress.line1,
        normalizedBillingAddress.line2,
        normalizedBillingAddress.city,
        normalizedBillingAddress.state,
        normalizedBillingAddress.postal_code,
        normalizedBillingAddress.country,
      ].filter(Boolean).join(", "),
    });

    if (updatedUserProfile) {
      setCurrentUserProfile(updatedUserProfile);
      setUserProfileAddressSeed(normalizeBillingAddress({
        line1: updatedUserProfile.addressLine1,
        line2: updatedUserProfile.addressLine2,
        city: updatedUserProfile.city,
        state: updatedUserProfile.state || "WA",
        postalCode: updatedUserProfile.zipcode,
        country: updatedUserProfile.country || "US",
      }));
    }
  };

  const runWithBillingAddress = async (resumeAction: (resolvedBillingAddress: any) => Promise<void> | void) => {
    const resolvedBillingAddress = hasCompleteBillingAddress(billingAddress)
      ? normalizeBillingAddress(billingAddress)
      : null;

    if (resolvedBillingAddress) {
      await resumeAction(resolvedBillingAddress);
      return true;
    }

    openBillingAddressDialog(resumeAction);
    return false;
  };

  const getBundleQuantity = (purchaseType: string) =>
    Math.max(1, Number(bundleQuantities[purchaseType] || 1));

  const getNextNurseAutomationQuantity = (currentQuantity: number, operation: string, minimumQuantity: number = 0) => {
    const normalizedQuantity = Math.max(0, Number(currentQuantity || 0));
    const normalizedMinimum = Math.max(0, Number(minimumQuantity || 0));
    if (operation === BillingConstants.PRICE_OPERATION.ADD) {
      return normalizedQuantity + 1;
    }

    if (normalizedQuantity <= normalizedMinimum) {
      return normalizedMinimum;
    }

    return Math.max(normalizedMinimum, normalizedQuantity - 1);
  };

  const getNurseAdditionalAssessmentCount = (assessmentCount: number) => Math.max(
    0,
    Number(assessmentCount || 0) - BillingConstants.NURSE_INCLUDED_RESIDENTS,
  );

  const getNurseAdditionalBlockCount = (assessmentCount: number) => getNurseAdditionalAssessmentCount(assessmentCount);

  const getNurseRecurringAmountForCount = (
    assessmentCount: number,
    options?: { waiveBase?: boolean }
  ) => {
    const normalizedCount = Math.max(0, Number(assessmentCount || 0));
    if (normalizedCount <= 0) {
      return 0;
    }

    const baseAmount = options?.waiveBase ? 0 : BillingConstants.NURSE_BASE_SUBSCRIPTION_PRICE;
    return baseAmount + (
      getNurseAdditionalBlockCount(normalizedCount) * BillingConstants.NURSE_ADDITIONAL_RESIDENT_SUBSCRIPTION_PRICE
    );
  };

  const subscriptionIncludedHomes = Math.max(0, Number(allowedHomes || 0) - Number(purchasedAllowedHomes || 0));
  const subscriptionIncludedResidents = Math.max(0, Number(allowedResidents || 0) - Number(purchasedAllowedResidents || 0));
  const subscriptionIncludedCaregivers = Math.max(0, Number(allowedCaregivers || 0) - Number(purchasedAllowedCaregivers || 0));
  const subscriptionIncludedResidentOverage = Math.max(
    0,
    subscriptionIncludedResidents - (subscriptionIncludedHomes * BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME)
  );
  const subscriptionIncludedCaregiverOverage = Math.max(
    0,
    subscriptionIncludedCaregivers - (subscriptionIncludedHomes * BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME)
  );
  const subscriptionIncludedResidentAddOnBlocks = Math.ceil(subscriptionIncludedResidentOverage / providerAddOnConfig.residentBlockSize);
  const subscriptionIncludedCaregiverAddOnBlocks = Math.ceil(subscriptionIncludedCaregiverOverage / providerAddOnConfig.staffBlockSize);
  const providerRecurringSubscriptionAmount = (subscriptionIncludedHomes * providerSingleHomeSubscriptionPrice)
    + (subscriptionIncludedResidentAddOnBlocks * providerAddOnConfig.residentBlockPrice)
    + (subscriptionIncludedCaregiverAddOnBlocks * providerAddOnConfig.staffBlockPrice)
    + (Number(adminTestSubscriptions || 0) * BillingConstants.SUBSCRIPTION_PRICE_BY_TYPE[BillingConstants.SUBSCRIPTION_TYPE.ADMIN_TEST]);
  const currentNurseAutomationCount = Math.max(0, Number(allowedResidents || 0));
  const nurseRecurringSubscriptionAmount = getNurseRecurringAmountForCount(currentNurseAutomationCount, {
    waiveBase: providerRecurringSubscriptionAmount > 0,
  });
  const hasRecurringProviderPlan = providerRecurringSubscriptionAmount > 0;
  const canRemoveOnePlan = false;
  const currentHomeUsage = Math.max(0, Number(currentHomeCount || 0));
  const currentResidentUsage = Math.max(0, Number(currentResidentCount || 0));
  const availableHomeSlots = Math.max(0, Number(remainingHomes || 0));
  const availableResidentSlots = Math.max(0, Number(remainingResidents || 0));
  const oneTimeBalanceItems = currentBalanceItems;
  const minimumProviderHomeQuantity = activeBillingRoleType === ROLE_TYPE_PROVIDER
    ? Math.max(isUserSubscribed ? subscriptionIncludedHomes : 1, currentHomeUsage)
    : 0;
  const minimumProviderResidentCapacity = activeBillingRoleType === ROLE_TYPE_PROVIDER
    ? Math.max(
      isUserSubscribed
        ? (subscriptionIncludedHomes * BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME) + subscriptionIncludedResidentOverage
        : 0,
      currentResidentUsage,
    )
    : 0;
  const minimumProviderResidentAddOnQuantity = Math.max(
    0,
    minimumProviderResidentCapacity - (minimumProviderHomeQuantity * BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME),
  );
  const minimumProviderCaregiverAddOnQuantity = isUserSubscribed ? subscriptionIncludedCaregiverAddOnBlocks : 0;
  const minimumNurseAutomationQuantity = hasCurrentNursePlan
    ? currentNurseAutomationCount
    : isNurseBillingRole
      ? BillingConstants.NURSE_INCLUDED_RESIDENTS
      : 0;

  const getMinimumPlanQuantity = (subscriptionType: string) => {
    if (subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME) {
      return minimumProviderHomeQuantity;
    }
    if (subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.ADD_RESIDENT) {
      return minimumProviderResidentAddOnQuantity;
    }
    if (subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.ADD_CAREGIVER) {
      return minimumProviderCaregiverAddOnQuantity;
    }
    if (subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN) {
      return minimumNurseAutomationQuantity;
    }
    return 0;
  };

  const getMaximumPlanQuantity = (subscriptionType: string) => {
    if (subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN) {
      return Number.POSITIVE_INFINITY;
    }
    return Number.POSITIVE_INFINITY;
  };

  const renderStepperButtonSlot = (
    visible: boolean,
    button: React.ReactNode,
    size: { width: string; height: string },
  ) => (
    visible ? button : <span style={{ display: "inline-block", width: size.width, height: size.height }} />
  );

  const getResolvedCurrentBalanceItems = (billingDetails: any, roleType: string) => {
    const serverItems = Array.isArray(billingDetails?.currentBalanceItems) ? billingDetails.currentBalanceItems : [];
    return serverItems;
  };

  const getDefaultPlanQuantitiesForRole = (roleType: string) => {
    const nextPlanQuantities = { ...INITIAL_PLAN_QUANTITIES };
    if (roleType === ROLE_TYPE_PROVIDER) {
      nextPlanQuantities[BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME] = 1;
    }
    if (isNurseStyleRoleType(roleType)) {
      nextPlanQuantities[BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN] = BillingConstants.NURSE_INCLUDED_RESIDENTS;
    }
    return nextPlanQuantities;
  };

  const getProviderPlanQuantitiesFromActiveCounts = (billingDetails: any, subscribed: boolean) => {
    const resolvedAllowedHomes = Number(billingDetails?.allowedHomes || 0);
    const resolvedPurchasedAllowedHomes = Number(billingDetails?.purchasedAllowedHomes || 0);
    const resolvedAllowedResidents = Number(billingDetails?.allowedResidents || 0);
    const resolvedPurchasedAllowedResidents = Number(billingDetails?.purchasedAllowedResidents || 0);
    const resolvedCurrentHomeCount = Math.max(0, Number(billingDetails?.currentHomeCount || 0));
    const resolvedCurrentResidentCount = Math.max(0, Number(billingDetails?.currentResidentCount || 0));
    const resolvedProviderHomeCount = Math.max(0, resolvedAllowedHomes - resolvedPurchasedAllowedHomes);
    const resolvedProviderIncludedResidents = resolvedProviderHomeCount * BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME;
    const resolvedProviderResidentAddOnCount = Math.max(
      0,
      Math.max(0, resolvedAllowedResidents - resolvedPurchasedAllowedResidents) - resolvedProviderIncludedResidents,
    );
    const homeQuantity = Math.max(subscribed ? resolvedProviderHomeCount : 1, resolvedCurrentHomeCount);
    const residentCapacityFloor = Math.max(
      subscribed ? (resolvedProviderIncludedResidents + resolvedProviderResidentAddOnCount) : 0,
      resolvedCurrentResidentCount,
    );
    const residentAddOnQuantity = Math.max(
      0,
      residentCapacityFloor - (homeQuantity * BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME),
    );

    return {
      homeQuantity,
      residentAddOnQuantity,
    };
  };

  const normalizeBillingCatalogByRoleForUi = (catalogByRole: Record<string, any> | null | undefined) => {
    if (!catalogByRole) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(catalogByRole).map(([roleKey, catalogValue]: [string, any]) => [
        roleKey,
        !catalogValue
          ? catalogValue
          : {
            ...catalogValue,
            oneTimeTab: catalogValue.oneTimeTab
              ? {
                ...catalogValue.oneTimeTab,
                tabHeader: "Automation Credits",
                cardTitle: "Automation Credits",
              }
              : catalogValue.oneTimeTab,
          },
      ]),
    );
  };

  const applyBillingDetails = (billingDetails: any, roleType: string) => {
    const nextPlanQuantities = getDefaultPlanQuantitiesForRole(roleType);
    const resolvedAllowedHomes = Number(billingDetails?.allowedHomes || 0);
    const resolvedPurchasedAllowedHomes = Number(billingDetails?.purchasedAllowedHomes || 0);
    const resolvedAllowedResidents = Number(billingDetails?.allowedResidents || 0);
    const resolvedPurchasedAllowedResidents = Number(billingDetails?.purchasedAllowedResidents || 0);
    const resolvedAllowedCaregivers = Number(billingDetails?.allowedCaregivers || 0);
    const resolvedPurchasedAllowedCaregivers = Number(billingDetails?.purchasedAllowedCaregivers || 0);
    const resolvedProviderHomeCount = Math.max(0, resolvedAllowedHomes - resolvedPurchasedAllowedHomes);
    const resolvedProviderIncludedResidents = resolvedProviderHomeCount * BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME;
    const resolvedProviderIncludedCaregivers = resolvedProviderHomeCount * BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME;
    const resolvedProviderResidentAddOnCount = Math.max(
      0,
      Math.max(0, resolvedAllowedResidents - resolvedPurchasedAllowedResidents) - resolvedProviderIncludedResidents,
    );
    const resolvedProviderCaregiverAddOnCount = Math.ceil(Math.max(
      0,
      Math.max(0, resolvedAllowedCaregivers - resolvedPurchasedAllowedCaregivers) - resolvedProviderIncludedCaregivers,
    ) / BillingConstants.PROVIDER_ADDITIONAL_CAREGIVER_BLOCK_SIZE);
    if (roleType === ROLE_TYPE_PROVIDER) {
      const providerPlanQuantities = getProviderPlanQuantitiesFromActiveCounts(billingDetails, !!billingDetails?.paidSubscriptionActive);
      nextPlanQuantities[BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME] = providerPlanQuantities.homeQuantity;
      nextPlanQuantities[BillingConstants.SUBSCRIPTION_TYPE.ADD_RESIDENT] = providerPlanQuantities.residentAddOnQuantity;
    }
    if (roleType === ROLE_TYPE_PROVIDER && billingDetails?.paidSubscriptionActive) {
      nextPlanQuantities[BillingConstants.SUBSCRIPTION_TYPE.ADD_CAREGIVER] = resolvedProviderCaregiverAddOnCount;
    }
    if (isNurseStyleRoleType(roleType) && resolvedAllowedResidents > 0) {
      nextPlanQuantities[BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN] = resolvedAllowedResidents;
    }

    setPlanQuantities(nextPlanQuantities);
    setTotalPrice(
      !billingDetails?.paidSubscriptionActive
        ? roleType === ROLE_TYPE_PROVIDER
          ? calculateCartTotal(nextPlanQuantities)
          : isNurseStyleRoleType(roleType)
            ? getNurseRecurringAmountForCount(
              nextPlanQuantities[BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN]
            )
            : 0
        : 0
    );
    setIsUserSubscribed(false);
    const fetchedFeatureCreditBalances = getBillingDetailsFeatureCreditBalances(billingDetails, roleType);
    setFeatureCreditBalances(fetchedFeatureCreditBalances);
    setCurrentBalanceItems(getResolvedCurrentBalanceItems(billingDetails, roleType));
    setBillingCatalogByRole(normalizeBillingCatalogByRoleForUi(billingDetails?.billingCatalogByRole));
    setWebsitePublishCreditsAvailable(Number(billingDetails?.websitePublishCreditsAvailable || 0));
    setWebsitePublishUnlockedHomeIds(Array.isArray(billingDetails?.websitePublishUnlockedHomeIds) ? billingDetails.websitePublishUnlockedHomeIds : []);
    setRemainingHomes(Number(billingDetails?.remainingHomes || 0));
    setRemainingResidents(Number(billingDetails?.remainingResidents || 0));
    setCurrentHomeCount(Number(billingDetails?.currentHomeCount || 0));
    setCurrentResidentCount(Number(billingDetails?.currentResidentCount || 0));
    setPaymentMethodSummary(billingDetails?.paymentMethodSummary || null);
    setBillingAddress(normalizeBillingAddress(billingDetails?.billingAddress || {}));
    setBillingRecoveryStatus(billingDetails?.billingRecoveryStatus || "healthy");
    setSubscriptionStatus(String(billingDetails?.subscriptionStatus || "").toLowerCase());
    setLastPaymentFailureReason(billingDetails?.lastPaymentFailureReason || "");
    setLastPaymentFailureAt(parseBillingDate(billingDetails?.lastPaymentFailureAt));
    setNextRetryAt(parseBillingDate(billingDetails?.nextRetryAt));
    setCanRetryPayment(!!billingDetails?.canRetryPayment);
    setCancelAtPeriodEnd(!!billingDetails?.cancelAtPeriodEnd);
    setPurchasedAllowedHomes(Number(billingDetails?.purchasedAllowedHomes || 0));
    setPurchasedAllowedResidents(Number(billingDetails?.purchasedAllowedResidents || 0));
    setPurchasedAllowedCaregivers(Number(billingDetails?.purchasedAllowedCaregivers || 0));
    setAdminTestSubscriptions(Number(billingDetails?.adminTestSubscriptions || 0));
    if (billingDetails?.nursePlanTier) {
      setCurrentNursePlanTier(billingDetails.nursePlanTier);
    } else {
      setCurrentNursePlanTier("");
    }

    if (billingDetails && billingDetails.paidSubscriptionActive) {
      setIsUserSubscribed(true);
      setAllowedHomes(billingDetails.allowedHomes);
      setAllowedResidents(billingDetails.allowedResidents);
      setAllowedCaregivers(Number(billingDetails?.allowedCaregivers || 0));
      setSubscriptionPrice(billingDetails.subscriptionPrice);
      setCurrentBillingPeriodStart(parseBillingDate(billingDetails.currentPeriodStart));
      setCurrentBillingPeriodEnd(parseBillingDate(billingDetails.currentPeriodEnd));
      setPaymentIntentType(billingDetails.intentType);
      setTrialEndDate(billingDetails.freeTrialUsed ? parseBillingDate(billingDetails.trialEndDate) : null);
      setSubscriptionCancelDate(billingDetails.cancelAt ? parseBillingDate(billingDetails.cancelAt) : null);
    } else {
      setIsUserSubscribed(false);
      setAllowedHomes(Number(billingDetails?.allowedHomes || 0));
      setAllowedResidents(Number(billingDetails?.allowedResidents || 0));
      setAllowedCaregivers(Number(billingDetails?.allowedCaregivers || 0));
      setSubscriptionPrice(0);
      setCurrentBillingPeriodStart(null);
      setCurrentBillingPeriodEnd(null);
      setTrialEndDate(null);
      setSubscriptionCancelDate(null);
      console.log("No active subscription found for user.");
    }
  };

  const loadPaymentHistoryPostRender = async (resolvedUserId: string) => {
    try {
      const paymentHistory = await backendAPIStore.getPaymentHistory(resolvedUserId);
      setInvoiceHistory(paymentHistory?.invoices || []);
      setFeatureCreditPurchaseHistory(paymentHistory?.featureCreditPurchases || []);
      setNextInvoiceCursor(paymentHistory?.nextInvoiceCursor || null);
      setNextFeatureCursor(paymentHistory?.nextFeatureCursor || null);
    } catch (error: any) {
      console.error("Failed to load payment history after page render", error);
    }
  };

  const syncOneTimeBalancesFromBilling = async () => {
    const billingDetails = await backendAPIStore.getSubscription(userId, activeBillingRoleType);
    const refreshedFeatureCreditBalances = getBillingDetailsFeatureCreditBalances(billingDetails, activeBillingRoleType);

    setFeatureCreditBalances(refreshedFeatureCreditBalances);
    setCurrentBalanceItems(getResolvedCurrentBalanceItems(billingDetails, activeBillingRoleType));
    setBillingCatalogByRole(normalizeBillingCatalogByRoleForUi(billingDetails?.billingCatalogByRole));
    setPurchasedAllowedResidents(Number(billingDetails?.purchasedAllowedResidents || 0));
    setPurchasedAllowedCaregivers(Number(billingDetails?.purchasedAllowedCaregivers || 0));
    setPurchasedAllowedHomes(Number(billingDetails?.purchasedAllowedHomes || 0));
    setAllowedResidents(Number(billingDetails?.allowedResidents || 0));
    setAllowedCaregivers(Number(billingDetails?.allowedCaregivers || 0));
    setAllowedHomes(Number(billingDetails?.allowedHomes || 0));
    setAdminTestSubscriptions(Number(billingDetails?.adminTestSubscriptions || 0));
    setRemainingResidents(Number(billingDetails?.remainingResidents || 0));
    setRemainingHomes(Number(billingDetails?.remainingHomes || 0));
    setCurrentHomeCount(Number(billingDetails?.currentHomeCount || 0));
    setCurrentResidentCount(Number(billingDetails?.currentResidentCount || 0));
    setWebsitePublishCreditsAvailable(Number(billingDetails?.websitePublishCreditsAvailable || 0));
    setWebsitePublishUnlockedHomeIds(Array.isArray(billingDetails?.websitePublishUnlockedHomeIds) ? billingDetails.websitePublishUnlockedHomeIds : []);
    setPaymentMethodSummary(billingDetails?.paymentMethodSummary || null);
  };

  const updateBundleQuantity = (purchaseType: string, updater: number | ((prev: number) => number)) => {
    setBundleQuantities((prev) => {
      const currentValue = Math.max(1, Number(prev[purchaseType] || 1));
      const resolvedValue = typeof updater === "function"
        ? (updater as (prev: number) => number)(currentValue)
        : updater;
      return {
        ...prev,
        [purchaseType]: Math.max(1, Number(resolvedValue) || 1),
      };
    });
  };

  const getSavedPaymentMethodLabel = () =>
    paymentMethodSummary?.displayName
    || (paymentMethodSummary?.last4 ? `${String(paymentMethodSummary.brand || "").toUpperCase()} ending in ${paymentMethodSummary.last4}` : paymentMethodSummary?.typeLabel)
    || "saved payment method";

  const formatBillingPeriod = (start: Date | null, end: Date | null) => {
    if (start && end) {
      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    }
    if (start) {
      return `Started ${start.toLocaleDateString()}`;
    }
    if (end) {
      return `Ends ${end.toLocaleDateString()}`;
    }
    return null;
  };

  const parseBillingDate = (value: any): Date | null => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === "number") {
      const parsedDate = new Date(value);
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    if (typeof value === "string") {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return null;
      }

      const numericValue = Number(trimmedValue);
      const parsedDate = Number.isNaN(numericValue) ? new Date(trimmedValue) : new Date(numericValue);
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    return null;
  };

  const openExternalUrl = (url?: string | null) => {
    if (!url) {
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const closeOneTimeCheckoutDialog = () => {
    setOneTimeCheckoutVisible(false);
    setSetUpIntentClientSecret("");
    setPaymentIntentType("");
    setBundleTaxDetails(null);
  };

  useEffect(() => {
    const setBillingDetails = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const requestedBillingTabIndex = getBillingTabIndexFromSearch(window.location.search);
      const defaultBillingTabIndex = requestedBillingTabIndex === 2 && activeBillingRoleType !== ROLE_TYPE_PROVIDER
        ? 0
        : requestedBillingTabIndex;

      setLoading(true);
      setTotalPrice(0);
      setPlanQuantities(getDefaultPlanQuantitiesForRole(activeBillingRoleType));
      setSetUpIntentClientSecret("");
      setPaymentIntentType("");
      setPaymentMode("subscription");
      setSubscriptionWizardStep(0);
      setSubscriptionCheckoutKind("standard");
      setPaymentReturnMessage("");
      setProcessingReturn(false);
      setBillingTabIndex(defaultBillingTabIndex);
      setPaymentMethodSummary(null);
      setBillingRecoveryStatus("healthy");
      setSubscriptionStatus("");
      setLastPaymentFailureReason("");
      setLastPaymentFailureAt(null);
      setCurrentBillingPeriodStart(null);
      setCurrentBillingPeriodEnd(null);
      setNextRetryAt(null);
      setCanRetryPayment(false);
      setCancelAtPeriodEnd(false);
      setCurrentNursePlanTier("");
      setFeatureCreditBalances({});
      setCurrentBalanceItems([]);
      setInvoiceHistory([]);
      setFeatureCreditPurchaseHistory([]);
      setNextInvoiceCursor(null);
      setNextFeatureCursor(null);
      setPurchasedAllowedHomes(0);
      setPurchasedAllowedResidents(0);
      setAllowedHomes(0);
      setAllowedResidents(0);
      setSubscriptionPrice(0);
      setAdminTestSubscriptions(0);
      setRemainingHomes(0);
      setRemainingResidents(0);
      setWebsitePublishCreditsAvailable(0);
      setSelectedBundlePurchaseType("");

      try {
        await backendAPIStore.init();
        const authenticated = await authStore.isUserLoggedIn();
        if (authenticated) {
          const resolvedUserId = authStore.getUserId();
          setUserId(resolvedUserId);
          const userDetails = await backendAPIStore.getUserProfile(resolvedUserId);
          if (!userDetails) {
            navigate(RouterAddress.PROFILE);
            return;
          }

          setUserProfileAddressSeed(normalizeBillingAddress({
            line1: userDetails.addressLine1,
            line2: userDetails.addressLine2,
            city: userDetails.city,
            state: userDetails.state || "WA",
            postalCode: userDetails.zipcode,
            country: userDetails.country || "US",
          }));

          setCurrentUserProfile(userDetails || null);

          const detectedRoleType = authStore.getUserRoleType() || ROLE_TYPE_PROVIDER;

          if (resolvedUserId) {
            const billingDetails = await backendAPIStore.getSubscription(resolvedUserId, detectedRoleType);
            applyBillingDetails(billingDetails, detectedRoleType);
            window.setTimeout(() => {
              void loadPaymentHistoryPostRender(resolvedUserId);
            }, 0);

            const setupIntent = searchParams.get('setup_intent');
            const paymentIntent = searchParams.get('payment_intent');
            const redirectStatus = searchParams.get('redirect_status');
            const purchase = searchParams.get('purchase');
            const oneTimeParam = String(searchParams.get('oneTime') || "").trim().toLowerCase();
            const requestedTab = String(searchParams.get('tab') || searchParams.get('billingTab') || "").trim().toLowerCase();
            const flow = searchParams.get('flow');
            const chargeMode = searchParams.get('chargeMode');

            if (
              ["true", "1", "yes"].includes(oneTimeParam)
              || ["credits", "credit", "automation-credits", "automation_credit"].includes(requestedTab)
            ) {
              setBillingTabIndex(1);
            } else if (purchase === "bundle") {
              setBillingTabIndex(1);
              setPaymentMode("bundle");
              setSelectedBundlePurchaseType(searchParams.get("bundleType") || "");
            }

            if (paymentIntent && redirectStatus === "succeeded" && flow === "subscription") {
              setProcessingReturn(true);
              if (chargeMode === "providerUpgrade") {
                await backendAPIStore.finalizeSubscriptionUpgradePayment({
                  userId: resolvedUserId,
                  paymentIntentId: paymentIntent,
                });
              } else if (chargeMode === "subscriptionUpdateCharge") {
                await backendAPIStore.finalizeSubscriptionPlanUpdatePayment({
                  userId: resolvedUserId,
                  paymentIntentId: paymentIntent,
                });
              } else {
                await backendAPIStore.createSubscription({
                  userId: resolvedUserId,
                  paymentIntentId: paymentIntent,
                });
              }

              navigate(RouterAddress.BILLING);
              window.location.reload();
            }

            if (setupIntent && redirectStatus === "succeeded" && flow === "subscription") {
              setProcessingReturn(true);
              await backendAPIStore.createSubscription({
                userId: resolvedUserId,
                setupIntent: setupIntent,
              });

              navigate(RouterAddress.BILLING);
              window.location.reload();
            }

            if (setupIntent && flow === "cardUpdate") {
              setProcessingReturn(true);
              await backendAPIStore.finalizeCardUpdate({
                userId: resolvedUserId,
                setupIntent: setupIntent,
              });

              navigate(RouterAddress.BILLING);
              window.location.reload();
            }

            if (paymentIntent && redirectStatus === "succeeded") {
              setProcessingReturn(true);
              const finalizeResult = await backendAPIStore.finalizeFeatureCreditPayment({
                userId: resolvedUserId,
                paymentIntentId: paymentIntent,
              });
              applyFinalizeFeaturePaymentResult(finalizeResult);
              await syncOneTimeBalancesFromBilling();
              setSetUpIntentClientSecret("");
              setPaymentIntentType("");
              setOneTimeCheckoutVisible(false);
              setUpdateSubscriptionFlag((prev) => !prev);
              navigate(`${RouterAddress.BILLING}?tab=credits`, { replace: true });
            }
          }
        } else {
          navigate(RouterAddress.LOGIN_PAGE);
          return;
        }
      } catch (error: any) {
        setProcessingReturn(false);
        toast.current?.show({ severity: "error", summary: "Error", detail: error.message || "Failed to fetch user profile data", life: 3000 });
      } finally {
        setLoading(false);
      }
    }

    setBillingDetails();
  }, [updateSubscriptionFlag, userId]);

  useEffect(() => {
    const shouldUseInlineCheckout = !["bundle"].includes(paymentMode);

    if (!setUpIntentClientSecret || !shouldUseInlineCheckout) {
      return;
    }

    const scrollTimer = window.setTimeout(() => {
      paymentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);

    return () => window.clearTimeout(scrollTimer);
  }, [setUpIntentClientSecret, paymentMode, billingTabIndex]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!billingToolsMenuRef.current?.contains(event.target as Node)) {
        setBillingToolsVisible(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, []);

  const options: StripeElementsOptions = {
    clientSecret: setUpIntentClientSecret,
    // Optional: customize the appearance of the Payment Element
    appearance: { theme: 'stripe' },
  };
  const applyFinalizeFeaturePaymentResult = (finalizeResult: any) => {
    setPaymentMethodSummary(finalizeResult?.paymentMethodSummary || null);
    if (isBundlePurchaseType(visibleOneTimeBundlePackages, finalizeResult?.purchaseType)) {
      setSelectedBundlePurchaseType(finalizeResult?.purchaseType || "");
      setFeatureCreditBalances(finalizeResult?.featureCreditBalances || featureCreditBalances);
      setPurchasedAllowedResidents(Number(finalizeResult?.purchasedAllowedResidents || purchasedAllowedResidents));
      setAllowedResidents(Number(finalizeResult?.allowedResidents || allowedResidents));
      setPaymentReturnMessage(
        finalizeResult?.alreadyProcessed
          ? `Your ${finalizeResult?.bundleName || "purchase"} was already recorded.`
          : `Your payment was received and ${finalizeResult?.bundleName || "purchase"} was added to your account.`
      );
      setPaymentMode("bundle");
      void syncOneTimeBalancesFromBilling();
      return;
    }

    setFeatureCreditBalances(finalizeResult?.featureCreditBalances || featureCreditBalances);
    setPaymentReturnMessage(
      finalizeResult?.alreadyProcessed
        ? "Your one-time purchase payment was already recorded."
        : `Your payment was received and ${finalizeResult?.bundleName || "your purchase"} was added to your account.`
    );
    setPaymentMode("bundle");
    void syncOneTimeBalancesFromBilling();
  };

  const finalizeOneTimePayment = async (paymentIntentId: string) => {
    const finalizeResult = await backendAPIStore.finalizeFeatureCreditPayment({
      userId,
      paymentIntentId,
    });
    applyFinalizeFeaturePaymentResult(finalizeResult);
    await syncOneTimeBalancesFromBilling();
    setSetUpIntentClientSecret("");
    setPaymentIntentType("");
    setOneTimeCheckoutVisible(false);
    setUpdateSubscriptionFlag((prev) => !prev);
  };

  const finalizeProviderUpgradePayment = async (paymentIntentId: string) => {
    await backendAPIStore.finalizeSubscriptionUpgradePayment({
      userId,
      paymentIntentId,
    });
    setSetUpIntentClientSecret("");
    setPaymentIntentType("");
    setSubscriptionWizardDialogVisible(false);
    setBillingAddressDialogVisible(false);
    setPaymentMode("subscription");
    setSubscriptionCheckoutKind("standard");
    setPaymentReturnMessage("Your subscription update was charged and your monthly subscription has been updated.");
    setUpdateSubscriptionFlag((prev) => !prev);
  };

  const finalizeSubscriptionPlanUpdatePayment = async (paymentIntentId: string) => {
    await backendAPIStore.finalizeSubscriptionPlanUpdatePayment({
      userId,
      paymentIntentId,
    });
    setSetUpIntentClientSecret("");
    setPaymentIntentType("");
    setPaymentMode("subscription");
    setSubscriptionCheckoutKind("standard");
    setPaymentReturnMessage("Your subscription update was charged and your monthly subscription has been updated.");
    setUpdateSubscriptionFlag((prev) => !prev);
  };

  const finalizeSubscriptionPayment = async (intentId: string, intentKind: "payment" | "setup" = "payment") => {
    await backendAPIStore.createSubscription(
      intentKind === "setup"
        ? {
          userId,
          setupIntent: intentId,
        }
        : {
          userId,
          paymentIntentId: intentId,
        }
    );
    setSetUpIntentClientSecret("");
    setPaymentIntentType("");
    setSubscriptionWizardDialogVisible(false);
    setBillingAddressDialogVisible(false);
    setPaymentMode("subscription");
    setSubscriptionCheckoutKind("standard");
    setPaymentReturnMessage("Your subscription payment was received and your subscription is now active.");
    setUpdateSubscriptionFlag((prev) => !prev);
  };

  const renderCheckoutFormContent = (submitLabel: string) => (
    setUpIntentClientSecret ? (
      <Elements stripe={stripePromise} options={options}>
        <CheckoutForm
          clientSecret={setUpIntentClientSecret}
          intentType={paymentIntentType}
          returnUrl={paymentCheckoutReturnUrl}
          submitLabel={submitLabel}
          telemetryAction={
            paymentMode === "subscription"
              ? (isUserSubscribed ? "billing_submit_subscription_update" : "billing_submit_subscription_payment")
              : paymentMode === "cardUpdate"
                ? "billing_submit_payment_method_update"
                : "billing_submit_bundle_payment"
          }
          onPaymentSuccess={isOneTimeCheckoutMode
            ? async (paymentIntentId: string) => {
              try {
                setProcessingReturn(true);
                await finalizeOneTimePayment(paymentIntentId);
                toast.current?.show({
                  severity: "success",
                  summary: "Payment completed",
                  detail: "Your purchase was added to your account.",
                  life: 3000,
                });
              } catch (error: any) {
                toast.current?.show({
                  severity: "error",
                  summary: "Finalize failed",
                  detail: error.message || "The payment went through, but we could not refresh your balances automatically yet.",
                  life: 4000,
                });
              } finally {
                setProcessingReturn(false);
              }
            }
            : async (intentId: string, intentKind: "payment" | "setup" = "payment") => {
              try {
                setProcessingReturn(true);
                if (paymentMode === "subscription" && !isUserSubscribed) {
                  setSubscriptionWizardStep(2);
                }
                if (subscriptionCheckoutKind === "providerUpgrade" && intentKind === "payment") {
                  await finalizeProviderUpgradePayment(intentId);
                } else if (subscriptionCheckoutKind === "subscriptionUpdateCharge" && intentKind === "payment") {
                  await finalizeSubscriptionPlanUpdatePayment(intentId);
                } else {
                  await finalizeSubscriptionPayment(intentId, intentKind);
                }
                toast.current?.show({
                  severity: "success",
                  summary: subscriptionCheckoutKind === "standard" ? "Subscription started" : "Subscription updated",
                  detail: subscriptionCheckoutKind === "providerUpgrade"
                    ? "Your subscription update was charged and applied."
                    : subscriptionCheckoutKind === "subscriptionUpdateCharge"
                      ? "Your subscription update was charged and applied."
                      : "Your subscription is now active.",
                  life: 3000,
                });
              } catch (error: any) {
                toast.current?.show({
                  severity: "error",
                  summary: "Finalize failed",
                  detail: error.message || "The payment went through, but we could not activate the subscription automatically yet.",
                  life: 4000,
                });
              } finally {
                setProcessingReturn(false);
              }
            }}
        />
      </Elements>
    ) : null
  );

  const renderPaymentSection = (submitLabel: string) => {
    if (subscriptionWizardDialogVisible && paymentMode === "subscription" && !isUserSubscribed) {
      return null;
    }

    return (
    setUpIntentClientSecret ? (
      <div ref={paymentRef}>
        {paymentMode === "subscription" && !isUserSubscribed ? (
          <Steps
            model={[
              { label: "Add Address" },
              { label: "Add Payment Method" },
              { label: "Start Subscription" },
            ]}
            activeIndex={subscriptionWizardStep}
            readOnly
            className="billing-subscription-wizard-steps mb-3"
          />
        ) : null}
        <Card
          className="mt-3"
          style={{ ...sectionCardStyle, padding: "0.5rem" }}
          header={cardTitleTemplate(
            paymentMode === "bundle"
              ? `Complete ${getBundleOption(selectedBundlePurchaseType)?.name || "Purchase"}`
              : "Payment Details"
          )}
        >
          {paymentMode === "subscription" && taxDetails && (
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "14px",
                padding: "0.95rem 1rem",
                marginBottom: "1rem",
              }}
            >
              {(isNonProratedSubscriptionUpdate || isImmediateProviderSubscriptionUpdate) && (
                <div style={{ color: "#596F80", marginBottom: "0.75rem" }}>
                  {isImmediateProviderSubscriptionUpdate
                    ? "This update charges only the plan difference today and updates your subscription immediately."
                    : "This update will apply on your next renewal. You will not be charged today."}
                </div>
              )}
              <div className="flex justify-content-between align-items-center">
                <span style={{ color: "#596F80" }}>
                  {isImmediateProviderSubscriptionUpdate
                    ? "Amount Due Today"
                    : isNonProratedSubscriptionUpdate
                      ? "New Monthly Subtotal"
                      : "Subtotal Before Tax"}
                </span>
                <strong style={{ color: "#0f172a" }}>${(isImmediateProviderSubscriptionUpdate ? immediateSubscriptionTaxableAmount : subscriptionTaxableAmount).toFixed(2)}</strong>
              </div>
              <div className="mt-2 flex justify-content-between align-items-center">
                <span style={{ color: "#596F80" }}>
                  {isImmediateProviderSubscriptionUpdate
                    ? "Tax Due Today"
                    : isNonProratedSubscriptionUpdate
                      ? "Estimated Monthly Tax"
                      : "Tax"}
                </span>
                <strong style={{ color: "#0f172a" }}>${Number(taxDetails?.tax || 0).toFixed(2)}</strong>
              </div>
              <div className="mt-2 flex justify-content-between align-items-center" style={{ paddingTop: "0.4rem", borderTop: "1px solid #e2e8f0" }}>
                <span style={{ color: "#9a3412", fontWeight: 700 }}>
                  {isImmediateProviderSubscriptionUpdate
                    ? "Total Due Today"
                    : isNonProratedSubscriptionUpdate
                      ? "New Monthly Total"
                      : "Total Due"}
                </span>
                <strong style={{ color: "#9a3412" }}>${Number(taxDetails?.total || (isImmediateProviderSubscriptionUpdate ? immediateSubscriptionTaxableAmount : subscriptionTaxableAmount) || 0).toFixed(2)}</strong>
              </div>
            </div>
          )}
          {renderCheckoutFormContent(submitLabel)}
        </Card>
      </div>
    ) : null
    );
  };

  const resolvedSubscriptionRoleType = isAdminBillingUser
    ? selectedActorRole
    : (activeBillingRoleType || userRoleType || ROLE_TYPE_PROVIDER);
  const selectedServices = (roleTypeSubscriptionMapping.get(resolvedSubscriptionRoleType)?.services || []).filter(Boolean);
  const selectedServiceTypes = (BillingConstants.ROLE_SERVICE_TYPE_MAP[resolvedSubscriptionRoleType] || []).filter(Boolean);
  const baseSelectedSubscriptionServices = isAdminBillingUser
    ? [...selectedServices, BillingConstants.ADMIN_TEST_SUBSCRIPTION_SERVICE]
    : selectedServices;
  const baseSelectedSubscriptionServiceTypes = isAdminBillingUser
    ? [...selectedServiceTypes, BillingConstants.SUBSCRIPTION_TYPE.ADMIN_TEST]
    : selectedServiceTypes;
  const hideCaregiverPlanSelection = isUserSubscribed
    && resolvedSubscriptionRoleType === ROLE_TYPE_CAREGIVER;
  const selectedSubscriptionServices = hideCaregiverPlanSelection
    ? []
    : baseSelectedSubscriptionServices;
  const selectedSubscriptionServiceTypes = hideCaregiverPlanSelection
    ? []
    : baseSelectedSubscriptionServiceTypes;
  const useSinglePlanBillingView = false;
  const useProviderPlanBuilder = resolvedSubscriptionRoleType === ROLE_TYPE_PROVIDER;
  const selectedProviderHomeCount = Number(planQuantities[BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME] || 0);
  const selectedProviderResidentAddOnCount = Number(planQuantities[BillingConstants.SUBSCRIPTION_TYPE.ADD_RESIDENT] || 0);
  const selectedProviderCaregiverAddOnCount = Number(planQuantities[BillingConstants.SUBSCRIPTION_TYPE.ADD_CAREGIVER] || 0);
  const selectedProviderCaregiverAddOnSeats = selectedProviderCaregiverAddOnCount * providerAddOnConfig.staffBlockSize;
  const providerHomeDelta = selectedProviderHomeCount - subscriptionIncludedHomes;
  const providerResidentDelta = selectedProviderResidentAddOnCount - subscriptionIncludedResidentOverage;
  const providerCaregiverDelta = selectedProviderCaregiverAddOnSeats - subscriptionIncludedCaregiverOverage;
  const providerCaregiverBlockDelta = selectedProviderCaregiverAddOnCount - subscriptionIncludedCaregiverAddOnBlocks;
  const providerHomeUpgradeDelta = Math.max(0, providerHomeDelta);
  const providerResidentUpgradeDelta = Math.max(0, providerResidentDelta);
  const providerCaregiverUpgradeDelta = Math.max(0, providerCaregiverDelta);
  const providerCaregiverBlockUpgradeDelta = Math.max(0, providerCaregiverBlockDelta);
  const hasProviderBaseSelected = providerRecurringSubscriptionAmount > 0
    || selectedProviderHomeCount > 0;
  const selectedNurseBasePrice = selectedNurseResidentCount > 0 && !hasProviderBaseSelected && !hasCurrentNursePlan
    ? BillingConstants.NURSE_BASE_SUBSCRIPTION_PRICE
    : 0;
  const selectedNurseExtraAutomationCount = getNurseAdditionalAssessmentCount(selectedNurseResidentCount);
  const selectedNurseAdditionalBlockCount = getNurseAdditionalBlockCount(selectedNurseResidentCount);
  const currentNurseAdditionalBlockCount = getNurseAdditionalBlockCount(currentNurseAutomationCount);
  const nurseAdditionalBlockDelta = selectedNurseAdditionalBlockCount - currentNurseAdditionalBlockCount;
  const currentNurseSubscriptionAmount = getNurseRecurringAmountForCount(currentNurseAutomationCount);
  const targetNurseSubscriptionAmount = selectedNurseResidentCount > 0
    ? getNurseRecurringAmountForCount(selectedNurseResidentCount)
    : 0;
  const nurseSubscriptionDeltaAmount = targetNurseSubscriptionAmount - currentNurseSubscriptionAmount;
  const providerSubscriptionBaseAmount = Math.max(0, Number(subscriptionPrice || 0) - nurseRecurringSubscriptionAmount);
  const onActorRoleChange = (roleType: string) => {
    setSelectedActorRole(roleType);
    const nextPlanQuantities = getDefaultPlanQuantitiesForRole(roleType);
    setPlanQuantities(nextPlanQuantities);
    setTotalPrice(
      roleType === ROLE_TYPE_PROVIDER
        ? providerSingleHomeSubscriptionPrice
        : isNurseStyleRoleType(roleType)
          ? getNurseRecurringAmountForCount(
            nextPlanQuantities[BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN]
          )
          : 0
    );
    setTaxDetails(null);
    setSetUpIntentClientSecret("");
    setPaymentIntentType("");
    setPaymentMode("subscription");
    setSubscriptionCheckoutKind("standard");
    setBillingTabIndex(getBillingTabIndexFromSearch(location.search));
  };

  const calculateCartTotal = (quantities: Record<string, number>) => {
    return Object.entries(quantities).reduce((sum, [type, quantity]) => {
      if (type === BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME) return sum + (providerSingleHomeSubscriptionPrice * quantity);
      if (type === BillingConstants.SUBSCRIPTION_TYPE.ADD_RESIDENT) return sum + (Math.ceil(quantity / providerAddOnConfig.residentBlockSize) * providerAddOnConfig.residentBlockPrice);
      if (type === BillingConstants.SUBSCRIPTION_TYPE.ADD_CAREGIVER) return sum + (quantity * providerAddOnConfig.staffBlockPrice);
      return sum + ((BillingConstants.SUBSCRIPTION_PRICE_BY_TYPE[type] || 0) * quantity);
    }, 0);
  };

  useEffect(() => {
    if (
      paymentMode !== "subscription"
      || activeBillingRoleType !== ROLE_TYPE_PROVIDER
      || isUserSubscribed
    ) {
      return;
    }

    const recalculatedTotal = calculateCartTotal(planQuantities);
    if (Math.abs(recalculatedTotal - Number(totalPrice || 0)) > 0.001) {
      setTotalPrice(recalculatedTotal);
    }
  }, [
    activeBillingRoleType,
    isUserSubscribed,
    paymentMode,
    planQuantities,
    providerAddOnConfig,
    providerSingleHomeSubscriptionPrice,
    totalPrice,
  ]);

  const targetProviderSubscriptionAmount = calculateCartTotal(planQuantities);
  const rawProviderSubscriptionDeltaAmount = targetProviderSubscriptionAmount - providerRecurringSubscriptionAmount;
  const providerSubscriptionDeltaAmount = Math.max(0, rawProviderSubscriptionDeltaAmount);

  const scrollToCartSection = () => {
    window.setTimeout(() => {
      paymentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  };

  const cardTitleTemplate = (title, price?, suffix: string = "/ month") => {
    return (
      <div className="flex flex-wrap align-items-center justify-content-between gap-2">
        <span className="text-xl text-900" style={{ textTransform: "capitalize" }}>{title}</span>
        <a className="flex flex-wrap justify-content-end gap-2">
          {price && <span className="text-xl text-900">${price}{suffix}</span>}
        </a>
      </div>
    );
  };

  const renderCapacityStepper = (
    label: string,
    value: number,
    onDecrease: () => void,
    onIncrease: () => void,
    accentColor: string,
    helperText: string,
    canDecrease: boolean,
    canIncrease: boolean = true,
  ) => (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: "16px",
        padding: "0.8rem 0.9rem",
        height: "100%",
      }}
    >
      <div style={{ fontSize: "0.78rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.6rem" }}>
        {label}
      </div>
      <div className="billing-nurse-capacity-stepper">
        {renderStepperButtonSlot(
          canDecrease,
          <Button
            icon="pi pi-minus"
            className="p-button-sm"
            style={{ height: "2.15rem", width: "2.15rem", borderRadius: "50%" }}
            type="button"
            onClick={onDecrease}
            severity="danger"
            outlined
            rounded
          />,
          { width: "2.15rem", height: "2.15rem" },
        )}
        <div
          className="billing-nurse-capacity-value"
          style={{
            minWidth: "3.2rem",
            textAlign: "center",
            fontWeight: 700,
            color: accentColor,
            fontSize: "1.55rem",
          }}
        >
          {value}
        </div>
        {renderStepperButtonSlot(
          canIncrease,
          <Button
            icon="pi pi-plus"
            className="p-button-sm"
            style={{ height: "2.15rem", width: "2.15rem", borderRadius: "50%" }}
            type="button"
            onClick={onIncrease}
            severity="warning"
            outlined
            rounded
          />,
          { width: "2.15rem", height: "2.15rem" },
        )}
      </div>
      <div className="mt-2" style={{ color: "#475569", lineHeight: 1.45, fontSize: "0.9rem" }}>
        {helperText}
      </div>
    </div>
  );

  const renderCompactPlanRow = (service: any, subscriptionType: string, idx: number) => {
    const servicePrice = Number(service?.price ?? BillingConstants.SUBSCRIPTION_PRICE_BY_TYPE[subscriptionType] ?? 0);
    const quantity = planQuantities[subscriptionType] || 0;
    const isAdminTestPlan = subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.ADMIN_TEST;
    const isSingleAddPlan = usesSingleAddSubscriptionControl(subscriptionType);
    const subscriptionCardContent = getSubscriptionCardContent(subscriptionType, service, sharedSubscriptionPlanContentByType);
    const isCorePlan = subscriptionCardContent.isCorePlan;
    const borderColor = isAdminTestPlan ? "#c4b5fd" : isCorePlan ? "#fdba74" : "#93c5fd";
    const rowBackground = isAdminTestPlan
      ? "linear-gradient(135deg, #f5f3ff 0%, #ffffff 100%)"
      : isCorePlan
        ? "linear-gradient(135deg, #fff7ed 0%, #ffffff 100%)"
        : "linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)";
    const badgeLabel = isAdminTestPlan ? "Admin Only" : subscriptionCardContent.badgeLabel;
    const badgeColor = isAdminTestPlan ? "#7c3aed" : isCorePlan ? "#9a3412" : "#1d4ed8";
    const providerBenefits = subscriptionCardContent.benefits;
    const displayMeta = getSubscriptionDisplayMeta(subscriptionType, service, sharedSubscriptionPlanContentByType);

    return (
      <AccordionTab
        key={`${service.name}-${idx}`}
        header={
          <div className="flex flex-wrap align-items-center justify-content-between gap-3" style={{ width: "100%", paddingRight: "0.5rem" }}>
            <div className="flex align-items-start gap-2">
              <div>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>{displayMeta.title}</div>
                <div style={{ color: "#64748b", fontSize: "0.9rem" }}>
                  {displayMeta.subtitle} • ${servicePrice} / month
                </div>
              </div>
              <span
                style={{
                  background: "#ffffff",
                  border: `1px solid ${borderColor}`,
                  color: badgeColor,
                  borderRadius: "999px",
                  padding: "0.2rem 0.6rem",
                  fontSize: "0.74rem",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {badgeLabel}
              </span>
            </div>
            <div className="flex align-items-center gap-2" onClick={(event) => event.stopPropagation()}>
              {isSingleAddPlan ? (
                quantity > 0 ? (
                  <span
                    style={{
                      minWidth: "5rem",
                      textAlign: "center",
                      fontWeight: 700,
                      color: "#9a3412",
                      background: "#fff7ed",
                      border: "1px solid #fdba74",
                      borderRadius: "999px",
                      padding: "0.45rem 0.85rem",
                    }}
                  >
                    Added
                  </span>
                ) : (
                  <Button
                    label="Add"
                    className="p-button-sm"
                    style={{ borderRadius: "999px", padding: "0.45rem 0.95rem" }}
                    onClick={() => updatedCart(subscriptionType, BillingConstants.PRICE_OPERATION.ADD)}
                    severity="warning"
                    outlined
                    rounded
                  />
                )
              ) : (
                <>
                  {renderStepperButtonSlot(
                    quantity > getMinimumPlanQuantity(subscriptionType),
                    <Button
                      icon="pi pi-minus"
                      className="p-button-sm"
                      style={{ height: "2rem", width: "2rem", borderRadius: "50%" }}
                      onClick={() => {
                        if (quantity > getMinimumPlanQuantity(subscriptionType)) {
                          updatedCart(subscriptionType, BillingConstants.PRICE_OPERATION.SUBTRACT);
                        }
                      }}
                      severity="danger"
                      outlined
                      rounded
                    />,
                    { width: "2rem", height: "2rem" },
                  )}
                  <span
                    style={{
                      minWidth: "3rem",
                      textAlign: "center",
                      fontWeight: 700,
                      color: "#0f172a",
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "999px",
                      padding: "0.35rem 0.65rem",
                    }}
                  >
                    {quantity}
                  </span>
                  {renderStepperButtonSlot(
                    quantity < getMaximumPlanQuantity(subscriptionType),
                    <Button
                      icon="pi pi-plus"
                      className="p-button-sm"
                      style={{ height: "2rem", width: "2rem", borderRadius: "50%" }}
                      onClick={() => updatedCart(subscriptionType, BillingConstants.PRICE_OPERATION.ADD)}
                      severity="warning"
                      outlined
                      rounded
                    />,
                    { width: "2rem", height: "2rem" },
                  )}
                </>
              )}
            </div>
          </div>
        }
        headerStyle={{
          borderRadius: "16px",
          border: `1px solid ${borderColor}`,
          background: rowBackground,
        }}
        contentStyle={{
          border: `1px solid ${borderColor}`,
          borderTop: "none",
          borderRadius: "0 0 16px 16px",
          background: "#ffffff",
          padding: "1.25rem 1.35rem 1rem 1.35rem",
        }}
      >
        <div style={{ color: "#596F80", lineHeight: 1.6, marginBottom: "0.75rem" }}>
          {subscriptionCardContent.description}
        </div>
        <div
          style={{
            background: isAdminTestPlan ? "#faf5ff" : isCorePlan ? "#fffaf0" : "#f8fbff",
            border: `1px solid ${borderColor}`,
            borderRadius: "14px",
            padding: "0.95rem 1rem",
          }}
        >
          <ul style={{ margin: 0, paddingLeft: "1.1rem", lineHeight: 1.55 }}>
            {providerBenefits.map((feature, includeIdx) => (
              <li key={includeIdx}>{feature}</li>
            ))}
          </ul>
        </div>
      </AccordionTab>
    );
  };

  const renderSubscriptionSignupCard = (service: any, subscriptionType: string, idx: number) => {
    if (isNurseBillingRole) {
      if (idx !== 0) {
        return null;
      }

      const isSimplifiedNurseSubscriptionView = hasCurrentNursePlan;

      return (
        <div
          key="nurse-single-plan-card"
          style={{
            borderRadius: "18px",
            border: "1px solid #fdba74",
            background: "linear-gradient(145deg, #fff7ed 0%, #ffffff 55%, #fff1d6 100%)",
            boxShadow: "0 18px 40px rgba(245, 158, 11, 0.14)",
            padding: "0.95rem 1rem",
          }}
        >
          <div className="flex flex-column gap-3">
            <div className="flex flex-wrap justify-content-between align-items-center gap-2">
              <div style={{ flex: "1 1 320px" }}>
                <div className="flex flex-wrap align-items-center gap-2">
                  <div style={{ fontSize: "1.15rem", fontWeight: 700, color: "#0f172a" }}>
                    {isSimplifiedNurseSubscriptionView ? "Change Hub Plan" : "Hub Plan"}
                  </div>
                  <span
                    style={{
                      background: "#ffffff",
                      border: "1px solid #fdba74",
                      color: "#9a3412",
                      borderRadius: "999px",
                      padding: "0.22rem 0.55rem",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {hasCurrentNursePlan ? "Current plan active" : "Start Here"}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <div className="flex flex-wrap align-items-center gap-2">
                <div style={{ color: "#475569", fontSize: "0.95rem", lineHeight: 1.6 }}>
                  {isSimplifiedNurseSubscriptionView
                    ? "Adjust your active resident capacity here."
                    : "Choose how many active residents you want included in your plan."}
                </div>
              </div>
            </div>

            {isSimplifiedNurseSubscriptionView && (
              <div
                style={{
                  background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 65%)",
                  borderRadius: "18px",
                  border: "1px solid #bfdbfe",
                  padding: "1.1rem 1.15rem",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap" }}>
                  <div>
                    <div style={{ color: "#185DA2", fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>
                      Subscription
                    </div>
                  </div>
                </div>
                <div
                  className="mt-3"
                  style={{
                    background: "#ffffff",
                    border: "1px solid #dbeafe",
                    borderRadius: "14px",
                    padding: "0.85rem 0.95rem",
                  }}
                >
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>Current Subscription Total</div>
                  <div className="mt-1" style={{ color: "#0f172a", fontWeight: 700 }}>
                    ${currentNurseSubscriptionAmount.toFixed(2)}/month
                  </div>
                </div>
              </div>
            )}

            <div className="grid align-items-stretch">
              <div className="col-12 flex justify-content-center align-items-center">
                <div style={{ width: "100%", maxWidth: "25rem" }}>
                  {renderCapacityStepper(
                    "Active Residents",
                    selectedNurseResidentCount,
                    () => updatedCart(BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN, BillingConstants.PRICE_OPERATION.SUBTRACT),
                    () => updatedCart(BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN, BillingConstants.PRICE_OPERATION.ADD),
                    "#9a3412",
                    hasCurrentNursePlan
                      ? "Adjust your active resident capacity here."
                      : `Extra resident: $${BillingConstants.NURSE_ADDITIONAL_RESIDENT_SUBSCRIPTION_PRICE}/month`,
                    selectedNurseResidentCount > minimumNurseAutomationQuantity,
                    true,
                  )}
                </div>
              </div>
            </div>

            {!isSimplifiedNurseSubscriptionView && (
              <div
                style={{
                  background: "#ffffff",
                  border: "1px solid #fed7aa",
                  borderRadius: "16px",
                  padding: "0.85rem 1rem",
                }}
              >
                <div className="flex justify-content-between align-items-center gap-3">
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Plan includes</div>
                  <Button
                    type="button"
                    label={showNursePlanIncludes ? "Hide" : "Show"}
                    text
                    size="small"
                    icon={showNursePlanIncludes ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                    iconPos="right"
                    onClick={() => setShowNursePlanIncludes((current) => !current)}
                  />
                </div>
                {showNursePlanIncludes && (
                  <div className="grid mt-2 mb-2">
                    {BillingConstants.NURSE_SERVICE_INCLUDE_SECTIONS.map((section) => (
                      <div key={section.title} className="col-12 md:col-6">
                        <div
                          style={{
                            border: "1px solid #ffedd5",
                            borderRadius: "14px",
                            padding: "0.8rem 0.9rem",
                            background: "#fffaf5",
                            height: "100%",
                          }}
                        >
                          <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.45rem" }}>{section.title}</div>
                          <div className="flex flex-column gap-2">
                            {section.items.map((item) => (
                              <div key={item} className="flex align-items-start gap-2" style={{ color: "#475569", lineHeight: 1.45, fontSize: "0.9rem" }}>
                                <i className="pi pi-check-circle" style={{ color: "#9a3412", marginTop: "0.1rem" }} />
                                <span>{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    const isProviderPlanBuilderView = resolvedSubscriptionRoleType === ROLE_TYPE_PROVIDER
      && subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME;

    if (
      resolvedSubscriptionRoleType === ROLE_TYPE_PROVIDER
      && (
        subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.ADD_RESIDENT
        || subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.ADD_CAREGIVER
      )
    ) {
      return null;
    }

    if (isProviderPlanBuilderView) {
      const selectedHomeQuantity = Number(planQuantities[BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME] || 0);
      const selectedResidentAddOnQuantity = Number(planQuantities[BillingConstants.SUBSCRIPTION_TYPE.ADD_RESIDENT] || 0);
      const selectedCaregiverAddOnQuantity = Number(planQuantities[BillingConstants.SUBSCRIPTION_TYPE.ADD_CAREGIVER] || 0);
      const includedResidents = selectedHomeQuantity * BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME;
      const includedCaregivers = selectedHomeQuantity * BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME;
      const selectedResidentCapacity = includedResidents + selectedResidentAddOnQuantity;
      const selectedCaregiverCapacity = includedCaregivers + (selectedCaregiverAddOnQuantity * providerAddOnConfig.staffBlockSize);
      const minimumHomeQuantity = minimumProviderHomeQuantity;
      const getMinimumResidentCapacityForHomeQuantity = (homeQuantity: number) => {
        const normalizedHomeQuantity = Math.max(0, Number(homeQuantity || 0));
        const includedResidentCapacity = normalizedHomeQuantity * BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME;
        const minimumResidentCapacityFromExistingAddOns = includedResidentCapacity + minimumProviderResidentAddOnQuantity;
        return Math.max(subscriptionIncludedResidents, minimumResidentCapacityFromExistingAddOns, currentResidentUsage);
      };
      const getMinimumCaregiverCapacityForHomeQuantity = (homeQuantity: number) => {
        const normalizedHomeQuantity = Math.max(0, Number(homeQuantity || 0));
        const includedCaregiverCapacity = normalizedHomeQuantity * BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME;
        const minimumCaregiverCapacityFromExistingAddOns = includedCaregiverCapacity + (minimumProviderCaregiverAddOnQuantity * providerAddOnConfig.staffBlockSize);
        return Math.max(subscriptionIncludedCaregivers, minimumCaregiverCapacityFromExistingAddOns);
      };
      const minimumResidentCapacity = getMinimumResidentCapacityForHomeQuantity(selectedHomeQuantity);
      const minimumCaregiverCapacity = getMinimumCaregiverCapacityForHomeQuantity(selectedHomeQuantity);
      const getResidentCapacityForHomeQuantity = (homeQuantity: number) => {
        const normalizedHomeQuantity = Math.max(0, Number(homeQuantity || 0));
        const homeDelta = normalizedHomeQuantity - selectedHomeQuantity;
        return selectedResidentCapacity + (homeDelta * BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME);
      };
      const getCaregiverCapacityForHomeQuantity = (homeQuantity: number) => {
        const normalizedHomeQuantity = Math.max(0, Number(homeQuantity || 0));
        const homeDelta = normalizedHomeQuantity - selectedHomeQuantity;
        return selectedCaregiverCapacity + (homeDelta * BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME);
      };

      const applyProviderPlannerQuantities = async (homeQuantity: number, residentCapacity: number, caregiverCapacity: number) => {
        setSetUpIntentClientSecret("");
        setPaymentIntentType("");
        setTaxDetails(null);

        const normalizedHomeQuantity = Math.max(0, Number(homeQuantity || 0));
        const minimumIncludedResidentCapacity = normalizedHomeQuantity * BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME;
        const minimumResidentCapacityForSelectedHomes = getMinimumResidentCapacityForHomeQuantity(normalizedHomeQuantity);
        const normalizedResidentCapacity = Math.max(
          minimumResidentCapacityForSelectedHomes,
          Math.max(minimumIncludedResidentCapacity, Math.max(0, Number(residentCapacity || 0)))
        );
        const normalizedResidentAddOnQuantity = Math.max(0, normalizedResidentCapacity - minimumIncludedResidentCapacity);
        const minimumIncludedCaregiverCapacity = normalizedHomeQuantity * BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME;
        const minimumCaregiverCapacityForSelectedHomes = getMinimumCaregiverCapacityForHomeQuantity(normalizedHomeQuantity);
        const normalizedCaregiverCapacity = Math.max(
          minimumCaregiverCapacityForSelectedHomes,
          Math.max(minimumIncludedCaregiverCapacity, Math.max(0, Number(caregiverCapacity || 0)))
        );
        const normalizedCaregiverAddOnQuantity = Math.max(
          0,
          Math.ceil((normalizedCaregiverCapacity - minimumIncludedCaregiverCapacity) / providerAddOnConfig.staffBlockSize),
        );

        const updatedQuantities = {
          ...planQuantities,
          [BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME]: normalizedHomeQuantity,
          [BillingConstants.SUBSCRIPTION_TYPE.ADD_RESIDENT]: normalizedResidentAddOnQuantity,
          [BillingConstants.SUBSCRIPTION_TYPE.ADD_CAREGIVER]: normalizedCaregiverAddOnQuantity,
        };

        setPlanQuantities(updatedQuantities);
        const total = isUserSubscribed
          ? Math.abs(calculateCartTotal(updatedQuantities) - providerRecurringSubscriptionAmount)
          : calculateCartTotal(updatedQuantities);
        setTotalPrice(total);
        scrollToCartSection();

        if (total <= 0) {
          return;
        }
      };

      const renderProviderStepper = (
        label: string,
        value: number,
        onDecrease: () => void,
        onIncrease: () => void,
        accentColor: string,
        helperText: string,
        canDecrease: boolean,
        canIncrease: boolean = true,
      ) => (
        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "14px",
            padding: "0.65rem 0.75rem",
            height: "100%",
          }}
        >
          <div style={{ fontSize: "0.72rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.4rem" }}>
            {label}
          </div>
          <div className="flex align-items-center justify-content-between gap-2">
            {renderStepperButtonSlot(
              canDecrease,
              <Button
                icon="pi pi-minus"
                className="p-button-sm"
                style={{ height: "1.95rem", width: "1.95rem", borderRadius: "50%" }}
                type="button"
                onClick={onDecrease}
                severity="danger"
                outlined
                rounded
              />,
              { width: "1.95rem", height: "1.95rem" },
            )}
            <div
              style={{
                minWidth: "2.6rem",
                textAlign: "center",
                fontWeight: 700,
                color: accentColor,
                fontSize: "1.3rem",
              }}
            >
              {value}
            </div>
            {renderStepperButtonSlot(
              canIncrease,
              <Button
                icon="pi pi-plus"
                className="p-button-sm"
                style={{ height: "1.95rem", width: "1.95rem", borderRadius: "50%" }}
                type="button"
                onClick={onIncrease}
                severity="warning"
                outlined
                rounded
              />,
              { width: "1.95rem", height: "1.95rem" },
            )}
          </div>
          <div className="mt-2" style={{ color: "#475569", lineHeight: 1.35, fontSize: "0.8rem" }}>
            {helperText}
          </div>
        </div>
      );

      return (
        <div
          key="provider-unsubscribed-plan-builder"
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            borderRadius: "18px",
            border: "1px solid #fdba74",
            background: "linear-gradient(145deg, #fff7ed 0%, #ffffff 55%, #fff1d6 100%)",
            boxShadow: "0 18px 40px rgba(245, 158, 11, 0.14)",
            padding: "0.7rem 0.8rem",
          }}
        >
          <div className="flex flex-wrap justify-content-between align-items-center gap-2">
            <div style={{ flex: "1 1 320px" }}>
              <div className="flex flex-wrap align-items-center gap-2">
                <div style={{ fontSize: "1.15rem", fontWeight: 700, color: "#0f172a" }}>Hub Plan</div>
                <span
                  style={{
                    background: "#ffffff",
                    border: "1px solid #fdba74",
                    color: "#9a3412",
                    borderRadius: "999px",
                    padding: "0.22rem 0.55rem",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  Start Here
                </span>
              </div>
            </div>
          </div>
          <div
            className="mt-3"
            style={{
              display: "none",
              background: "#ffffff",
              border: "1px solid #fed7aa",
              borderRadius: "16px",
              padding: "0.85rem 1rem",
            }}
          >
            <div className="flex justify-content-between align-items-center gap-3">
              <div style={{ fontWeight: 700, color: "#0f172a" }}>Plan includes</div>
              <Button
                type="button"
                label={showProviderPlanIncludes ? "Hide" : "Show"}
                text
                size="small"
                icon={showProviderPlanIncludes ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                iconPos="right"
                onClick={() => setShowProviderPlanIncludes((current) => !current)}
              />
            </div>
            {showProviderPlanIncludes && (
              <div className="grid mt-2 mb-2">
                {BillingConstants.PROVIDER_SERVICE_INCLUDE_SECTIONS.map((section) => (
                  <div key={section.title} className="col-12 md:col-6">
                    <div
                      style={{
                        border: "1px solid #ffedd5",
                        borderRadius: "14px",
                        padding: "0.8rem 0.9rem",
                        background: "#fffaf5",
                        height: "100%",
                      }}
                    >
                      <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.45rem" }}>{section.title}</div>
                      <div className="flex flex-column gap-2">
                        {section.items.map((item) => (
                          <div key={item} style={{ color: "#475569", lineHeight: 1.45, fontSize: "0.9rem" }}>
                            • <span dangerouslySetInnerHTML={{ __html: item }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="grid" style={{ display: "none" }}>
              {BillingConstants.getProviderServices(billingBrandConfig)[0].includes.map((item) => (
                <div key={item} className="col-12 md:col-6">
                  <div style={{ color: "#475569", lineHeight: 1.45, fontSize: "0.9rem" }}>
                    • <span dangerouslySetInnerHTML={{ __html: item }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid mt-3">
            <div className="col-12 md:col-6 xl:col-4">
              {renderProviderStepper(
                "Homes",
                selectedHomeQuantity,
                () => applyProviderPlannerQuantities(selectedHomeQuantity - 1, getResidentCapacityForHomeQuantity(selectedHomeQuantity - 1), getCaregiverCapacityForHomeQuantity(selectedHomeQuantity - 1)),
                () => applyProviderPlannerQuantities(selectedHomeQuantity + 1, getResidentCapacityForHomeQuantity(selectedHomeQuantity + 1), getCaregiverCapacityForHomeQuantity(selectedHomeQuantity + 1)),
                "#9a3412",
                selectedHomeQuantity > 0
                  ? `${selectedHomeQuantity} home${selectedHomeQuantity === 1 ? "" : "s"} includes ${includedResidents} resident and ${includedCaregivers} staff`
                  : `Each home includes ${BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME} residents and ${BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME} staff`,
                selectedHomeQuantity > minimumHomeQuantity,
              )}
            </div>
            <div className="col-12 md:col-6 xl:col-4">
              {renderProviderStepper(
                "Residents",
                selectedResidentCapacity,
                () => applyProviderPlannerQuantities(selectedHomeQuantity, selectedResidentCapacity - providerAddOnConfig.residentBlockSize, selectedCaregiverCapacity),
                () => applyProviderPlannerQuantities(selectedHomeQuantity, selectedResidentCapacity + providerAddOnConfig.residentBlockSize, selectedCaregiverCapacity),
                "#1d4ed8",
                `Extra ${providerAddOnConfig.residentBlockSize} residents: $${providerAddOnConfig.residentBlockPrice}/month`,
                selectedResidentCapacity > minimumResidentCapacity,
              )}
            </div>
            <div className="col-12 md:col-6 xl:col-4">
              {renderProviderStepper(
                "Staff",
                selectedCaregiverCapacity,
                () => applyProviderPlannerQuantities(selectedHomeQuantity, selectedResidentCapacity, selectedCaregiverCapacity - providerAddOnConfig.staffBlockSize),
                () => applyProviderPlannerQuantities(selectedHomeQuantity, selectedResidentCapacity, selectedCaregiverCapacity + providerAddOnConfig.staffBlockSize),
                "#c2410c",
                `Extra ${providerAddOnConfig.staffBlockSize} staff: $${providerAddOnConfig.staffBlockPrice}/month`,
                selectedCaregiverCapacity > minimumCaregiverCapacity,
              )}
            </div>
          </div>
          <div
            className="mt-3"
            style={{
              background: "#ffffff",
              border: "1px solid #fed7aa",
              borderRadius: "16px",
              padding: "0.85rem 1rem",
            }}
          >
            <div className="flex justify-content-between align-items-center gap-3">
              <div style={{ fontWeight: 700, color: "#0f172a" }}>Plan includes</div>
              <Button
                type="button"
                label={showProviderPlanIncludes ? "Hide" : "Show"}
                text
                size="small"
                icon={showProviderPlanIncludes ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                iconPos="right"
                onClick={() => setShowProviderPlanIncludes((current) => !current)}
              />
            </div>
            {showProviderPlanIncludes && (
              <div className="grid mt-2 mb-2">
                {BillingConstants.PROVIDER_SERVICE_INCLUDE_SECTIONS.map((section) => (
                  <div key={section.title} className="col-12 md:col-6">
                    <div
                      style={{
                        border: "1px solid #ffedd5",
                        borderRadius: "14px",
                        padding: "0.8rem 0.9rem",
                        background: "#fffaf5",
                        height: "100%",
                      }}
                    >
                      <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.45rem" }}>{section.title}</div>
                      <div className="flex flex-column gap-2">
                        {section.items.map((item) => (
                          <div key={item} style={{ color: "#475569", lineHeight: 1.45, fontSize: "0.9rem" }}>
                            • <span dangerouslySetInnerHTML={{ __html: item }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    const servicePrice = Number(service?.price ?? BillingConstants.SUBSCRIPTION_PRICE_BY_TYPE[subscriptionType] ?? 0);
    const quantity = planQuantities[subscriptionType] || 0;
    const isSingleAddPlan = usesSingleAddSubscriptionControl(subscriptionType);
    const subscriptionCardContent = getSubscriptionCardContent(subscriptionType, service, sharedSubscriptionPlanContentByType);
    const isCorePlan = subscriptionCardContent.isCorePlan;
    const isAdminTestPlan = subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.ADMIN_TEST;
    const displayMeta = getSubscriptionDisplayMeta(subscriptionType, service, sharedSubscriptionPlanContentByType);
    const benefits = subscriptionCardContent.benefits;
    const theme = isAdminTestPlan
      ? {
        borderColor: "#c4b5fd",
        background: "linear-gradient(145deg, #f5f3ff 0%, #ffffff 60%, #faf5ff 100%)",
        panelBackground: "#faf5ff",
        badgeLabel: "Admin Only",
        badgeColor: "#7c3aed",
      }
      : isCorePlan
        ? {
          borderColor: "#fdba74",
          background: "linear-gradient(145deg, #fff7ed 0%, #ffffff 55%, #fff1d6 100%)",
          panelBackground: "#fffaf0",
          badgeLabel: subscriptionCardContent.badgeLabel,
          badgeColor: "#9a3412",
        }
        : {
          borderColor: "#93c5fd",
          background: "linear-gradient(145deg, #eff6ff 0%, #ffffff 60%, #eef4ff 100%)",
          panelBackground: "#f8fbff",
          badgeLabel: subscriptionCardContent.badgeLabel,
          badgeColor: "#1d4ed8",
        };

    return (
      <div
        key={`${service.name}-${idx}`}
        className="mt-3"
        style={{
          borderRadius: "22px",
          border: `1px solid ${theme.borderColor}`,
          background: theme.background,
          boxShadow: isAdminTestPlan ? "0 16px 34px rgba(124, 58, 237, 0.12)" : isCorePlan ? "0 18px 40px rgba(245, 158, 11, 0.14)" : "0 16px 34px rgba(37, 99, 235, 0.10)",
          padding: "1.25rem",
        }}
      >
        <div className="flex flex-wrap justify-content-between align-items-start gap-3">
          <div style={{ flex: "1 1 320px" }}>
            <div className="flex flex-wrap align-items-center gap-2">
              <div style={{ fontSize: "1.35rem", fontWeight: 700, color: "#0f172a" }}>{displayMeta.title}</div>
              <span
                style={{
                  background: "#ffffff",
                  border: `1px solid ${theme.borderColor}`,
                  color: theme.badgeColor,
                  borderRadius: "999px",
                  padding: "0.28rem 0.7rem",
                  fontSize: "0.76rem",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {theme.badgeLabel}
              </span>
            </div>
            <div className="mt-2" style={{ color: "#475569", fontSize: "1rem" }}>
              {displayMeta.subtitle}
            </div>
            {subscriptionCardContent.description && (
              <div className="mt-3" style={{ color: "#334155", lineHeight: 1.7, maxWidth: "46rem" }}>
                {subscriptionCardContent.description}
              </div>
            )}
          </div>
          <div
            style={{
              minWidth: "220px",
              background: "#ffffff",
              border: `1px solid ${theme.borderColor}`,
              borderRadius: "18px",
              padding: "1rem",
            }}
          >
            <div style={{ color: "#64748b", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Monthly Price
            </div>
            <div className="mt-2" style={{ fontSize: "2rem", fontWeight: 700, color: "#0f172a" }}>
              ${servicePrice}
            </div>
            <div style={{ color: "#64748b" }}>per month</div>
            <div className="mt-3 flex justify-content-center align-items-center gap-2">
              {isSingleAddPlan ? (
                quantity > 0 ? (
                  <div
                    style={{
                      minWidth: "6rem",
                      textAlign: "center",
                      fontWeight: 700,
                      color: "#9a3412",
                      background: "#fff7ed",
                      border: "1px solid #fdba74",
                      borderRadius: "999px",
                      padding: "0.55rem 0.95rem",
                    }}
                  >
                    Added
                  </div>
                ) : (
                  <Button
                    label="Add"
                    className="p-button-sm"
                    style={{ borderRadius: "999px", padding: "0.55rem 1rem" }}
                    onClick={() => updatedCart(subscriptionType, BillingConstants.PRICE_OPERATION.ADD)}
                    severity="warning"
                    outlined
                    rounded
                  />
                )
              ) : (
                <>
                  {renderStepperButtonSlot(
                    quantity > getMinimumPlanQuantity(subscriptionType),
                    <Button
                      icon="pi pi-minus"
                      className="p-button-sm"
                      style={{ height: "2.2rem", width: "2.2rem", borderRadius: "50%" }}
                      onClick={() => {
                        if (quantity > getMinimumPlanQuantity(subscriptionType)) {
                          updatedCart(subscriptionType, BillingConstants.PRICE_OPERATION.SUBTRACT);
                        }
                      }}
                      severity="danger"
                      outlined
                      rounded
                    />,
                    { width: "2.2rem", height: "2.2rem" },
                  )}
                  <div
                    style={{
                      minWidth: "3.2rem",
                      textAlign: "center",
                      fontWeight: 700,
                      color: "#0f172a",
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: "999px",
                      padding: "0.45rem 0.7rem",
                    }}
                  >
                    {quantity}
                  </div>
                  {renderStepperButtonSlot(
                    quantity < getMaximumPlanQuantity(subscriptionType),
                    <Button
                      icon="pi pi-plus"
                      className="p-button-sm"
                      style={{ height: "2.2rem", width: "2.2rem", borderRadius: "50%" }}
                      onClick={() => updatedCart(subscriptionType, BillingConstants.PRICE_OPERATION.ADD)}
                      severity="warning"
                      outlined
                      rounded
                    />,
                    { width: "2.2rem", height: "2.2rem" },
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <div
          className="mt-4"
          style={{
            background: theme.panelBackground,
            border: `1px solid ${theme.borderColor}`,
            borderRadius: "18px",
            padding: "1rem 1.05rem",
          }}
        >
          <div style={{ fontSize: "0.82rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.8rem" }}>
            {subscriptionCardContent.isAddOnPlan ? "What This Adds" : "Included Every Month"}
          </div>
          <div className="grid">
            {benefits.map((feature, includeIdx) => (
              <div key={includeIdx} className="col-12 md:col-6">
                <div className="flex align-items-start gap-2" style={{ color: "#334155", lineHeight: 1.6 }}>
                  <i className="pi pi-check-circle" style={{ color: theme.badgeColor, marginTop: "0.15rem" }} />
                  <span>{feature}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderOneTimeQuantityControl = (quantity: number, setQuantity: (updater: any) => void) => (
    <div
      className="billing-ncp-quantity-control"
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <Button
        icon="pi pi-minus"
        className="p-button-sm billing-ncp-quantity-btn"
        style={{ height: "2rem", width: "2rem", borderRadius: "50%" }}
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setQuantity((prev: number) => Math.max(1, prev - 1));
        }}
        severity="danger"
        outlined
        rounded
      />
      <span style={{ minWidth: "1.75rem", textAlign: "center", fontWeight: 700, color: "#0f172a" }}>{quantity}</span>
      <Button
        icon="pi pi-plus"
        className="p-button-sm billing-ncp-quantity-btn"
        style={{ height: "2rem", width: "2rem", borderRadius: "50%" }}
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setQuantity((prev: number) => prev + 1);
        }}
        severity="warning"
        outlined
        rounded
      />
    </div>
  );

  const renderBundlePackages = () => (
    <div className="grid">
      {filteredOneTimeBundlePackages.length === 0 && (
        <div className="col-12">
          <div style={{ background: "#ffffff", border: "1px dashed #cbd5e1", borderRadius: "16px", padding: "1rem 1.1rem", color: "#596F80", lineHeight: 1.7 }}>
            {oneTimeTabConfig.emptyState}
          </div>
        </div>
      )}
      {filteredOneTimeBundlePackages.map((bundle) => {
        const quantity = getBundleQuantity(bundle.purchaseType);
        const bundleSubtotal = bundle.price * quantity;
        const isAutomationCreditBundle = bundle.purchaseType === "nurse_automation_pack";
        const automationCreditsPerPack = BillingConstants.PROVIDER_NURSE_AUTOMATION_PACK_SIZE;
        const purchaseUnitLabel = isAutomationCreditBundle ? "credit" : "pack";
        const purchaseUnitLabelPlural = isAutomationCreditBundle ? "credits" : "packs";
        return (
          <div key={bundle.name} className={isAutomationCreditBundle ? "col-12" : "col-12 md:col-6"}>
            <div
              style={{
                background: bundle.background,
                border: `1px solid ${bundle.borderColor}`,
                borderRadius: "16px",
                padding: "1rem",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                className="flex gap-3 align-items-start"
                style={{ justifyContent: isAutomationCreditBundle ? "center" : "space-between" }}
              >
                <div style={{ textAlign: isAutomationCreditBundle ? "center" : "left" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>{bundle.name}</div>
                  <div className="mt-1" style={{ color: "#596F80", lineHeight: 1.6 }}>{bundle.subtitle}</div>
                  {isAutomationCreditBundle && automationCreditsPerPack > 1 && (
                    <div
                      className="mt-2"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.45rem",
                        background: "#fff7ed",
                        border: "1px solid #fdba74",
                        color: "#9a3412",
                        borderRadius: "999px",
                        padding: "0.35rem 0.7rem",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                      }}
                    >
                      <i className="pi pi-bolt" style={{ fontSize: "0.72rem" }} />
                      <span>Available in multiples of {automationCreditsPerPack} credits</span>
                    </div>
                  )}
                </div>
                {bundle.badge && !isAutomationCreditBundle && (
                  <span
                    style={{
                      background: "#ffffff",
                      border: `1px solid ${bundle.borderColor}`,
                      color: bundle.accentColor,
                      borderRadius: "999px",
                      padding: "0.3rem 0.65rem",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {bundle.badge}
                  </span>
                )}
              </div>
              <div className="grid align-items-center mt-3">
                <div className="col-12" style={{ textAlign: isAutomationCreditBundle ? "center" : "left" }}>
                  <div style={{ color: "#64748b", fontSize: "0.85rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Quantity
                  </div>
                  <div className="mt-2 flex justify-content-center" onClick={(event) => event.stopPropagation()}>
                    {renderOneTimeQuantityControl(quantity, (updater: any) => updateBundleQuantity(bundle.purchaseType, updater))}
                  </div>
                </div>
                <div className="col-12" style={{ textAlign: isAutomationCreditBundle ? "center" : "left" }}>
                  <div className="flex flex-wrap justify-content-center align-items-start gap-4" style={{ color: "#334155", fontSize: "0.9rem" }}>
                    {!isAutomationCreditBundle && (
                      <>
                        <div>
                          <div style={{ color: "#64748b", fontWeight: 700 }}>Price</div>
                          <div className="mt-1" style={{ color: bundle.accentColor, fontWeight: 700 }}>${bundle.price}</div>
                        </div>
                        <div>
                          <div style={{ color: "#64748b", fontWeight: 700 }}>Selected</div>
                          <div className="mt-1" style={{ fontWeight: 700 }}>
                            {quantity} {quantity === 1 ? purchaseUnitLabel : purchaseUnitLabelPlural} = ${bundleSubtotal}
                          </div>
                        </div>
                      </>
                    )}
                    {isAutomationCreditBundle && (
                      <div className="flex flex-column gap-1" style={{ fontWeight: 700 }}>
                        <div>Each Credit: ${bundle.price / automationCreditsPerPack}</div>
                        <div>Total: ${bundleSubtotal}</div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-12 flex justify-content-center">
                  <Button
                    label={`Buy ${quantity} ${quantity === 1 ? purchaseUnitLabel : purchaseUnitLabelPlural}`}
                    severity="warning"
                    rounded
                    style={{ width: "100%", maxWidth: "320px", justifyContent: "center", fontWeight: 700 }}
                    loading={loadingPaymentIntent && paymentMode === "bundle" && selectedBundlePurchaseType === bundle.purchaseType}
                    onClick={() => onBuyBundleSubmit(bundle)}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const sectionCardStyle = {
    border: "1px solid #e3edf5",
    boxShadow: "0 14px 32px rgba(24, 93, 162, 0.08)",
    borderRadius: "16px",
    padding: "0.35rem",
  } as const;

  const updatedCart = async (subscriptionType, operation?) => {
    setSetUpIntentClientSecret("");
    setPaymentIntentType("");
    setTaxDetails(null);

    const currentQuantity = planQuantities[subscriptionType] || 0;
    const isNurseAutomationSelection = subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.NURSE_AI_ASSESSMENT;
    const isNurseResidentPlanSelection = subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN;
    const minimumQuantity = getMinimumPlanQuantity(subscriptionType);
    const updatedQuantity = isNurseAutomationSelection
      ? getNextNurseAutomationQuantity(currentQuantity, operation, minimumQuantity)
      : operation === BillingConstants.PRICE_OPERATION.ADD
        ? currentQuantity + 1
        : Math.max(currentQuantity - 1, minimumQuantity);

    const updatedQuantities = {
      ...planQuantities,
      [subscriptionType]: updatedQuantity,
    };

    setPlanQuantities(updatedQuantities);
    const total = (isNurseAutomationSelection || isNurseResidentPlanSelection) && isNurseBillingRole
      ? getNurseRecurringAmountForCount(updatedQuantity, { waiveBase: hasProviderBaseSelected || hasCurrentNursePlan })
      : calculateCartTotal(updatedQuantities);
    setTotalPrice(total);

    if (total <= 0) {
      return;
    }
  };

  const onProceedToPaymentSubmit = async (providedBillingAddress?: any) => {
    if (totalPrice <= 0) {
      toast.current?.show({
        severity: "error", summary: "No plan selected",
        detail: "Please select at least one plan to continue.", life: 3000
      });
      return;
    }

    if (
      !isUserSubscribed &&
      resolvedSubscriptionRoleType === ROLE_TYPE_PROVIDER &&
      (planQuantities[BillingConstants.SUBSCRIPTION_TYPE.ADMIN_TEST] || 0) < 1 &&
      (planQuantities[BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME] || 0) < minimumProviderHomeQuantity
    ) {
      toast.current?.show({
        severity: "error",
        summary: "Plan Required",
        detail: `Please include at least ${minimumProviderHomeQuantity} home${minimumProviderHomeQuantity === 1 ? "" : "s"} in your plan.`,
        life: 3500,
      });
      return;
    }
    if (!isUserSubscribed && resolvedSubscriptionRoleType === ROLE_TYPE_PROVIDER) {
      const selectedHomeQuantity = Number(planQuantities[BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME] || 0);
      const selectedResidentAddOnQuantity = Number(planQuantities[BillingConstants.SUBSCRIPTION_TYPE.ADD_RESIDENT] || 0);
      const selectedResidentCapacity = (selectedHomeQuantity * BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME) + selectedResidentAddOnQuantity;
      if (selectedResidentCapacity < minimumProviderResidentCapacity) {
        toast.current?.show({
          severity: "error",
          summary: "Resident Capacity Required",
          detail: `Please include at least ${minimumProviderResidentCapacity} resident${minimumProviderResidentCapacity === 1 ? "" : "s"} in your plan.`,
          life: 3500,
        });
        return;
      }
    }

    const processSubscriptionCheckout = async (resolvedBillingAddress: any) => {
      const confirmSubscriptionUpdate = ({
        subtotalBeforeTax,
        computedTaxAmount,
        computedTotalWithTax,
        isRenewalPreview,
      }: {
        subtotalBeforeTax: number;
        computedTaxAmount: number;
        computedTotalWithTax: number;
        isRenewalPreview: boolean;
      }) => new Promise<boolean>((resolve) => {
        confirmDialog({
          header: "Confirm Subscription Update",
          style: { width: "92vw", maxWidth: "560px" },
          message: (
            <div style={{ lineHeight: 1.7, width: "100%", minWidth: "420px" }}>
              {paymentMethodSummary?.id && (
                <>
                  <div style={{ color: "#596F80" }}>Saved card</div>
                  <div style={{ fontWeight: 700, color: "#0f172a", marginTop: "0.25rem" }}>{getSavedPaymentMethodLabel()}</div>
                </>
              )}
              {isUserSubscribed && (
                <div style={{ marginTop: paymentMethodSummary?.id ? "1rem" : "0.25rem", color: "#596F80" }}>
                  {isRenewalPreview
                    ? "This update will apply on your next renewal. You will not be charged today."
                    : "This update charges only the plan difference today and updates your subscription immediately."}
                </div>
              )}
              <div style={{ marginTop: paymentMethodSummary?.id ? "1rem" : "0.25rem", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "0.9rem 1rem" }}>
                <div className="flex justify-content-between align-items-center">
                  <span style={{ color: "#596F80" }}>{isRenewalPreview ? "New Monthly Subtotal" : "Amount Due Today"}</span>
                  <strong style={{ color: "#0f172a" }}>${subtotalBeforeTax.toFixed(2)}</strong>
                </div>
                <div className="mt-2 flex justify-content-between align-items-center">
                  <span style={{ color: "#596F80" }}>{isRenewalPreview ? "Estimated Monthly Tax" : "Tax Due Today"}</span>
                  <strong style={{ color: "#0f172a" }}>${computedTaxAmount.toFixed(2)}</strong>
                </div>
                <div className="mt-2 flex justify-content-between align-items-center">
                  <span style={{ color: "#9a3412", fontWeight: 700 }}>{isRenewalPreview ? "New Monthly Total" : "Total Due Today"}</span>
                  <strong style={{ color: "#9a3412" }}>${computedTotalWithTax.toFixed(2)}</strong>
                </div>
              </div>
            </div>
          ),
          acceptLabel: "Continue",
          rejectLabel: "Cancel",
          acceptClassName: "p-button-warning",
          accept: () => resolve(true),
          reject: () => resolve(false),
          onHide: () => resolve(false),
        });
      });

      setLoadingPaymentIntent(true);
      try {
        setPaymentMode("subscription");
        if (!isUserSubscribed) {
          setSubscriptionWizardStep(1);
        }
        const requestedCheckoutRoleType = isAdminBillingUser
          ? selectedActorRole
          : (activeBillingRoleType || userRoleType || ROLE_TYPE_PROVIDER);
        const checkoutRoleType = requestedCheckoutRoleType;
        const isSubscribedProviderPlanUpdate = isUserSubscribed
          && resolvedSubscriptionRoleType === ROLE_TYPE_PROVIDER;
        const isSubscribedNursePlanUpdate = isUserSubscribed
          && isNurseStyleRoleType(requestedCheckoutRoleType);
        const checkoutAmount = isUserSubscribed
          ? isNurseBillingRole
            ? targetNurseSubscriptionAmount
            : resolvedSubscriptionRoleType === ROLE_TYPE_PROVIDER
              ? targetProviderSubscriptionAmount
              : totalPrice
          : totalPrice;
        const checkoutAllowedHomes = isSubscribedProviderPlanUpdate
          ? providerHomeDelta
          : (planQuantities[BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME] || 0);
        const checkoutAllowedResidents = isSubscribedProviderPlanUpdate
          ? providerResidentDelta + (providerHomeDelta * BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME)
          : calculateTotalResidentsAllowed();
        const checkoutAllowedCaregivers = isSubscribedProviderPlanUpdate
          ? providerCaregiverDelta + (providerHomeDelta * BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME)
          : calculateTotalCaregiversAllowed();
        const checkoutRequestAmount = isSubscribedProviderPlanUpdate
          ? providerSubscriptionDeltaAmount
          : isSubscribedNursePlanUpdate
            ? nurseSubscriptionDeltaAmount
            : checkoutAmount;
        const nextRecurringPreviewAmount = !isUserSubscribed
          ? checkoutAmount
          : isSubscribedProviderPlanUpdate
            ? checkoutRequestAmount
            : isSubscribedNursePlanUpdate
              ? checkoutRequestAmount
              : resolvedSubscriptionRoleType === ROLE_TYPE_PROVIDER
                ? checkoutAmount
                : isReplacementPriceRole
                  ? checkoutAmount
                  : checkoutAmount;
        const taxBaseAmount = nextRecurringPreviewAmount;
        const latestTaxDetails = await fetchTaxDetailsForAmount(taxBaseAmount, resolvedBillingAddress);
        const computedTaxAmount = Number(latestTaxDetails?.tax || 0);
        const computedTotalWithTax = Number(latestTaxDetails?.total || taxBaseAmount || 0);
        if (isUserSubscribed) {
          const didConfirm = await confirmSubscriptionUpdate({
            subtotalBeforeTax: taxBaseAmount,
            computedTaxAmount,
            computedTotalWithTax,
            isRenewalPreview: isUserSubscribed && !isSubscribedProviderPlanUpdate && !isSubscribedNursePlanUpdate,
          });
          if (!didConfirm) {
            return;
          }
        }
        setTaxDetails(latestTaxDetails);
        if (isSubscribedProviderPlanUpdate) {
          if (rawProviderSubscriptionDeltaAmount <= 0) {
            toast.current?.show({
              severity: "warn",
              summary: "Upgrade required",
              detail: "Provider subscription updates must increase your plan total. Negative or zero plan changes are not allowed right now.",
              life: 3500,
            });
            return;
          }

          if (paymentMethodSummary?.id) {
            const result = await backendAPIStore.chargeSavedSubscriptionUpgrade({
              userId,
              purchaseType: PROVIDER_SUBSCRIPTION_UPGRADE_PURCHASE_TYPE,
              brandKey: authExperience.brandKey,
              amount: checkoutRequestAmount,
              allowedHomes: checkoutAllowedHomes,
              allowedResidents: checkoutAllowedResidents,
              allowedCaregivers: checkoutAllowedCaregivers,
            });
            setPaymentMethodSummary(result?.paymentMethodSummary || paymentMethodSummary);
            toast.current?.show({
              severity: "success",
              summary: "Subscription updated successfully",
              detail: "Your subscription update was charged and applied successfully.",
              life: 3000
            });
            setSubscriptionCheckoutKind("standard");
            setUpdateSubscriptionFlag((prev) => !prev);
            return;
          }

          const paymentSettings = await backendAPIStore.createSubscriptionUpgradePaymentIntent({
            userId,
            purchaseType: PROVIDER_SUBSCRIPTION_UPGRADE_PURCHASE_TYPE,
            brandKey: authExperience.brandKey,
            amount: checkoutRequestAmount,
            allowedHomes: checkoutAllowedHomes,
            allowedResidents: checkoutAllowedResidents,
            allowedCaregivers: checkoutAllowedCaregivers,
            billingAddress: resolvedBillingAddress,
          });

          if (paymentSettings && paymentSettings.clientSecret) {
            setSubscriptionCheckoutKind("providerUpgrade");
            setSetUpIntentClientSecret(paymentSettings.clientSecret);
            setPaymentIntentType(paymentSettings.intentType);
            setBillingTabIndex(0);
            return;
          }

          toast.current?.show({ severity: "error", summary: "Failed to generate upgrade payment", detail: "Please try again.", life: 3000 });
          return;
        }

        if (isSubscribedNursePlanUpdate) {
          if (paymentMethodSummary?.id) {
            const result = await backendAPIStore.chargeSavedSubscriptionPlanUpdate({
              userId,
              purchaseType: SUBSCRIPTION_PLAN_UPDATE_PURCHASE_TYPE,
              amount: checkoutRequestAmount,
              targetSubscriptionAmount: targetNurseSubscriptionAmount,
              roleType: checkoutRoleType,
              nursePlanTier: BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN,
              nurseAutomationCount: 0,
              allowedResidents: selectedNurseResidentCount,
            });
            setPaymentMethodSummary(result?.paymentMethodSummary || paymentMethodSummary);
            toast.current?.show({
              severity: "success",
              summary: "Subscription updated successfully",
              detail: "Your subscription update was charged and applied successfully.",
              life: 3000
            });
            setSubscriptionCheckoutKind("standard");
            setUpdateSubscriptionFlag((prev) => !prev);
            return;
          }

          const paymentSettings = await backendAPIStore.createSubscriptionPlanUpdatePaymentIntent({
            userId,
            purchaseType: SUBSCRIPTION_PLAN_UPDATE_PURCHASE_TYPE,
            amount: checkoutRequestAmount,
            targetSubscriptionAmount: targetNurseSubscriptionAmount,
            roleType: checkoutRoleType,
            nursePlanTier: BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN,
            nurseAutomationCount: 0,
            allowedResidents: selectedNurseResidentCount,
            billingAddress: resolvedBillingAddress,
          });

          if (paymentSettings && paymentSettings.clientSecret) {
            setSubscriptionCheckoutKind("subscriptionUpdateCharge");
            setSetUpIntentClientSecret(paymentSettings.clientSecret);
            setPaymentIntentType(paymentSettings.intentType);
            setBillingTabIndex(0);
            return;
          }

          toast.current?.show({ severity: "error", summary: "Failed to generate subscription update payment", detail: "Please try again.", life: 3000 });
          return;
        }

        setSubscriptionCheckoutKind("standard");
        const paymentSettings = await backendAPIStore.createSetUpIntent({
          userId: userId,
          brandKey: authExperience.brandKey,
          roleType: checkoutRoleType,
          amount: checkoutRequestAmount,
          nursePlanTier: isNurseStyleRoleType(requestedCheckoutRoleType)
            ? BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN
            : undefined,
          nurseAutomationCount: isNurseStyleRoleType(requestedCheckoutRoleType)
            ? 0
            : undefined,
          allowedHomes: checkoutAllowedHomes,
          allowedResidents: isNurseStyleRoleType(requestedCheckoutRoleType)
            ? selectedNurseResidentCount
            : checkoutAllowedResidents,
          allowedCaregivers: checkoutAllowedCaregivers,
          adminTestSubscriptions: planQuantities[BillingConstants.SUBSCRIPTION_TYPE.ADMIN_TEST] || 0,
          billingAddress: resolvedBillingAddress,
        });

        if (paymentSettings && paymentSettings.clientSecret) {
          setSetUpIntentClientSecret(paymentSettings.clientSecret);
          setPaymentIntentType(paymentSettings.intentType);
          setBillingTabIndex(0);
        } else if (paymentSettings && paymentSettings.isSubscriptionCompleted) {
          toast.current?.show({ severity: "success", summary: "Subscription updated successfully", detail: "Your subscription has been updated successfully.", life: 3000 });
          setUpdateSubscriptionFlag(!updateSubscriptionFlag);
          window.location.reload();
        } else {
          toast.current?.show({ severity: "error", summary: "Failed to generate subscription", detail: "Please try again.", life: 3000 });
        }
      } catch (error) {
        console.error("Failed to update payment settings:", error);
        toast.current?.show({ severity: "error", summary: "Failed to update payment details", detail: "Please try again.", life: 3000 });
      } finally {
        setLoadingPaymentIntent(false);
      }
    };

    if (providedBillingAddress && hasCompleteBillingAddress(providedBillingAddress)) {
      await processSubscriptionCheckout(normalizeBillingAddress(providedBillingAddress));
      return;
    }

    await runWithBillingAddress(processSubscriptionCheckout);
  };

  const onBuyBundleSubmit = async (bundle: any, quantityOverride?: number) => {
    const resolvedBundleQuantity = quantityOverride ?? getBundleQuantity(bundle.purchaseType) ?? 1;
    const bundleQuantity = Math.max(1, Number(resolvedBundleQuantity || 1));
    const bundleSubtotal = bundle.price * bundleQuantity;
    let purchaseStarted = false;
    const hasBillingAddress = await runWithBillingAddress(async (resolvedBillingAddress) => {
      let latestTaxDetails: any = null;
      try {
        latestTaxDetails = await fetchTaxDetailsForAmount(bundleSubtotal, resolvedBillingAddress);
      } catch (error) {
        latestTaxDetails = null;
      }

      const taxAmount = Number(latestTaxDetails?.tax || 0);
      const totalWithTax = latestTaxDetails ? Number(latestTaxDetails.total || 0) : bundleSubtotal;
      setBundleTaxDetails(latestTaxDetails);

      if (paymentMethodSummary?.id) {
        confirmDialog({
          header: `Confirm ${bundle.name}`,
          style: { width: "92vw", maxWidth: "560px" },
          message: (
            <div style={{ lineHeight: 1.7, width: "100%", minWidth: "420px" }}>
              <div style={{ color: "#596F80" }}>Saved card</div>
              <div style={{ fontWeight: 700, color: "#0f172a", marginTop: "0.25rem" }}>{getSavedPaymentMethodLabel()}</div>
              <div style={{ marginTop: "1rem", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "0.9rem 1rem" }}>
                <div className="flex justify-content-between align-items-center">
                  <span style={{ color: "#596F80" }}>{bundleQuantity} {bundle.name}{bundleQuantity > 1 ? "s" : ""}</span>
                  <strong style={{ color: "#0f172a" }}>${bundleSubtotal.toFixed(2)}</strong>
                </div>
                <div className="mt-2 flex justify-content-between align-items-center">
                  <span style={{ color: "#596F80" }}>Tax</span>
                  <strong style={{ color: "#0f172a" }}>${taxAmount.toFixed(2)}</strong>
                </div>
                <div className="mt-2 flex justify-content-between align-items-center">
                  <span style={{ color: "#9a3412", fontWeight: 700 }}>Total</span>
                  <strong style={{ color: "#9a3412" }}>${totalWithTax.toFixed(2)}</strong>
                </div>
              </div>
            </div>
          ),
          acceptClassName: "p-button-warning p-button-rounded",
          rejectClassName: "p-button-text",
          acceptLabel: "Charge Saved Card",
          rejectLabel: "Cancel",
          accept: async () => {
            setLoadingPaymentIntent(true);
            try {
              const result = await backendAPIStore.chargeSavedFeatureCredits({
                userId,
                purchaseType: bundle.purchaseType,
                bundlePurchaseType: bundle.purchaseType,
                bundleQuantity,
                quantity: bundleQuantity,
              });
              setPaymentMethodSummary(result?.paymentMethodSummary || paymentMethodSummary);
              setFeatureCreditBalances(result?.featureCreditBalances || featureCreditBalances);
              setPurchasedAllowedResidents(Number(result?.purchasedAllowedResidents || purchasedAllowedResidents));
              setAllowedResidents(Number(result?.allowedResidents || allowedResidents));
              setSelectedBundlePurchaseType(bundle.purchaseType);
              setPaymentReturnMessage(
                result?.alreadyProcessed
                  ? `This ${bundle.name} purchase was already recorded.`
                  : `Charged ${getSavedPaymentMethodLabel()}. ${bundleQuantity} ${bundle.name}${bundleQuantity > 1 ? "s were" : " was"} added to your account.`
              );
              await syncOneTimeBalancesFromBilling();
              setUpdateSubscriptionFlag((prev) => !prev);
              toast.current?.show({
                severity: "success",
                summary: `${bundle.name} added`,
                detail: `Charged ${getSavedPaymentMethodLabel()}.`,
                life: 3500,
              });
              purchaseStarted = true;
            } catch (error: any) {
              toast.current?.show({
                severity: "error",
                summary: "Unable to charge saved card",
                detail: error.message || "Please update your payment method and try again.",
                life: 4000,
              });
            } finally {
              setLoadingPaymentIntent(false);
            }
          }
        });
        purchaseStarted = true;
        return;
      }

      setLoadingPaymentIntent(true);
      try {
        setPaymentMode("bundle");
        setSelectedBundlePurchaseType(bundle.purchaseType);
        const paymentSettings = await backendAPIStore.createFeatureCreditsPaymentIntent({
          userId,
          purchaseType: bundle.purchaseType,
          bundlePurchaseType: bundle.purchaseType,
          bundleQuantity,
          quantity: bundleQuantity,
          billingAddress: resolvedBillingAddress,
        });

        if (paymentSettings?.clientSecret) {
          setSetUpIntentClientSecret(paymentSettings.clientSecret);
          setPaymentIntentType(paymentSettings.intentType);
          setBillingTabIndex(1);
          setOneTimeCheckoutVisible(true);
          purchaseStarted = true;
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Payment setup failed",
            detail: "Unable to start the bundle purchase right now.",
            life: 3000,
          });
        }
      } catch (error: any) {
        toast.current?.show({
          severity: "error",
          summary: "Payment setup failed",
          detail: error.message || "Unable to start the bundle purchase right now.",
          life: 3000,
        });
      } finally {
        setLoadingPaymentIntent(false);
      }
    });
    return hasBillingAddress && purchaseStarted;
  };

  const onUpdateCardSubmit = async (targetTabIndex: number = 0, providedBillingAddress?: any) => {
    const startCardUpdate = async (resolvedBillingAddress: any) => {
      setLoadingPaymentIntent(true);
      try {
        setPaymentDetailsVisible(false);
        setBillingToolsVisible(false);
        setPaymentMode("cardUpdate");
        const paymentSettings = await backendAPIStore.createCardUpdateSetupIntent({ userId, billingAddress: resolvedBillingAddress });
        if (paymentSettings?.clientSecret) {
          setSetUpIntentClientSecret(paymentSettings.clientSecret);
          setPaymentIntentType(paymentSettings.intentType);
          setBillingTabIndex(targetTabIndex);
        } else {
          toast.current?.show({ severity: "error", summary: "Unable to update card", detail: "Please try again.", life: 3000 });
        }
      } catch (error: any) {
        toast.current?.show({ severity: "error", summary: "Unable to update card", detail: error.message || "Please try again.", life: 3000 });
      } finally {
        setLoadingPaymentIntent(false);
      }
    };

    if (providedBillingAddress && hasCompleteBillingAddress(providedBillingAddress)) {
      await startCardUpdate(normalizeBillingAddress(providedBillingAddress));
      return;
    }

    await runWithBillingAddress(startCardUpdate);
  };

  const onRetryPaymentSubmit = async () => {
    setLoadingPaymentIntent(true);
    try {
      await backendAPIStore.retryFailedPayment({ userId });
      toast.current?.show({ severity: "success", summary: "Retry requested", detail: "We started a payment retry for your latest invoice.", life: 3000 });
      setUpdateSubscriptionFlag((prev) => !prev);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Retry failed", detail: error.message || "Unable to retry payment right now.", life: 3000 });
    } finally {
      setLoadingPaymentIntent(false);
    }
  };

  const onCancelSubscriptionSubmit = async () => {
    if (!cancelDialogReason.trim()) {
      setCancelDialogError("Please select a cancellation reason.");
      return;
    }

    if (cancelDialogReason === "Other" && !cancelDialogOtherReason.trim()) {
      setCancelDialogError("Please share your reason.");
      return;
    }

    const cancelReason = cancelDialogReason === "Other"
      ? cancelDialogOtherReason.trim()
      : cancelDialogReason.trim();

    setLoadingPaymentIntent(true);
    try {
      await backendAPIStore.cancelSubscription({ userId, cancelReason });
      toast.current?.show({ severity: "success", summary: "Cancellation scheduled", detail: "Your subscription will cancel at the end of the current billing period.", life: 4000 });
      setCancelDialogVisible(false);
      setCancelDialogReason("");
      setCancelDialogOtherReason("");
      setCancelDialogError("");
      setUpdateSubscriptionFlag((prev) => !prev);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Cancellation failed", detail: error.message || "Unable to cancel subscription right now.", life: 3000 });
    } finally {
      setLoadingPaymentIntent(false);
    }
  };

  const onResumeSubscriptionSubmit = async () => {
    setLoadingPaymentIntent(true);
    try {
      await backendAPIStore.resumeSubscription({ userId });
      toast.current?.show({ severity: "success", summary: "Subscription resumed", detail: "Your subscription renewal has been restored.", life: 3000 });
      setUpdateSubscriptionFlag((prev) => !prev);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Resume failed", detail: error.message || "Unable to resume subscription right now.", life: 3000 });
    } finally {
      setLoadingPaymentIntent(false);
    }
  };

  const onRemoveSubscriptionPlanSubmit = async (planType: "provider" | "nurse") => {
    setLoadingPaymentIntent(true);
    try {
      await backendAPIStore.removeSubscriptionPlan({ userId, planType });
      toast.current?.show({
        severity: "success",
        summary: `${planType === "provider" ? "Provider" : "Nurse"} plan removed`,
        detail: `Your subscription was updated and the ${planType} plan was removed.`,
        life: 3500,
      });
      setUpdateSubscriptionFlag((prev) => !prev);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Plan removal failed",
        detail: error.message || "Unable to update the subscription right now.",
        life: 3000,
      });
    } finally {
      setLoadingPaymentIntent(false);
    }
  };

  const openCancelReasonDialog = (mode: "end_of_period") => {
    setCancelDialogMode(mode);
    setCancelDialogReason("");
    setCancelDialogOtherReason("");
    setCancelDialogError("");
    setCancelDialogVisible(true);
  };

  const onLoadMoreInvoices = async () => {
    if (!nextInvoiceCursor) {
      return;
    }

    setLoadingMoreInvoices(true);
    try {
      const paymentHistory = await backendAPIStore.getPaymentHistory(userId, {
        invoiceCursor: nextInvoiceCursor,
        invoiceLimit: 10,
      });
      setInvoiceHistory((prev) => [...prev, ...(paymentHistory?.invoices || [])]);
      setNextInvoiceCursor(paymentHistory?.nextInvoiceCursor || null);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Unable to load invoices", detail: error.message || "Please try again.", life: 3000 });
    } finally {
      setLoadingMoreInvoices(false);
    }
  };

  const onLoadMoreFeaturePurchases = async () => {
    if (!nextFeatureCursor) {
      return;
    }

    setLoadingMoreFeatureHistory(true);
    try {
      const paymentHistory = await backendAPIStore.getPaymentHistory(userId, {
        featureCursor: nextFeatureCursor,
        featureLimit: 10,
      });
      setFeatureCreditPurchaseHistory((prev) => [...prev, ...(paymentHistory?.featureCreditPurchases || [])]);
      setNextFeatureCursor(paymentHistory?.nextFeatureCursor || null);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Unable to load purchases", detail: error.message || "Please try again.", life: 3000 });
    } finally {
      setLoadingMoreFeatureHistory(false);
    }
  };

  const calculateTotalResidentsAllowed = () => {
    const singleHomePlanQuantity = planQuantities[BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME] || 0;
    const additionalResidentQuantity = planQuantities[BillingConstants.SUBSCRIPTION_TYPE.ADD_RESIDENT] || 0;
    return additionalResidentQuantity + (singleHomePlanQuantity * BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME);
  }

  const calculateTotalCaregiversAllowed = () => {
    const singleHomePlanQuantity = planQuantities[BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME] || 0;
    const additionalCaregiverQuantity = planQuantities[BillingConstants.SUBSCRIPTION_TYPE.ADD_CAREGIVER] || 0;
    return (additionalCaregiverQuantity * BillingConstants.PROVIDER_ADDITIONAL_CAREGIVER_BLOCK_SIZE)
      + (singleHomePlanQuantity * BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME);
  }

  const isNursePlanCart = isNurseStyleRoleType(resolvedSubscriptionRoleType);
  const isImmediateProviderSubscriptionUpdate = isUserSubscribed
    && resolvedSubscriptionRoleType === ROLE_TYPE_PROVIDER;
  const isImmediateNurseSubscriptionUpdate = isUserSubscribed
    && isNursePlanCart;
  const isImmediateSubscriptionUpdate = isImmediateProviderSubscriptionUpdate || isImmediateNurseSubscriptionUpdate;
  const isNonProratedSubscriptionUpdate = isUserSubscribed && !isImmediateProviderSubscriptionUpdate;
  const isReplacementSubscriptionUpdate = isUserSubscribed
    && isReplacementPriceRole;
  const complementaryRecurringAmount = isUserSubscribed
    ? isReplacementPriceRole
      ? providerSubscriptionBaseAmount
      : 0
    : 0;
  const cartLineItems = isNursePlanCart
    ? [
      ...(selectedNurseBasePrice > 0 ? [{
        key: "nurse-base-plan",
        name: "Hub Plan",
        quantity: 1,
        unitPrice: selectedNurseBasePrice,
        lineTotal: selectedNurseBasePrice,
      }] : []),
      ...((hasCurrentNursePlan ? nurseAdditionalBlockDelta !== 0 : selectedNurseAdditionalBlockCount > 0) ? [{
        key: "nurse-additional-residents",
        name: "Additional Active Residents",
        quantity: Math.abs(hasCurrentNursePlan ? nurseAdditionalBlockDelta : selectedNurseAdditionalBlockCount),
        unitPrice: BillingConstants.NURSE_ADDITIONAL_RESIDENT_SUBSCRIPTION_PRICE,
        lineTotal: (hasCurrentNursePlan ? nurseAdditionalBlockDelta : selectedNurseAdditionalBlockCount) * BillingConstants.NURSE_ADDITIONAL_RESIDENT_SUBSCRIPTION_PRICE,
      }] : []),
    ]
    : resolvedSubscriptionRoleType === ROLE_TYPE_PROVIDER && isUserSubscribed
      ? [
        ...(providerHomeUpgradeDelta > 0 ? [{
          key: "provider-home-plan-change",
          name: "Home Plan Change",
          quantity: providerHomeUpgradeDelta,
          unitPrice: providerSingleHomeSubscriptionPrice,
          lineTotal: providerHomeUpgradeDelta * providerSingleHomeSubscriptionPrice,
        }] : []),
        ...(providerResidentUpgradeDelta > 0 ? [{
          key: "provider-resident-plan-change",
          name: "Resident Plan Change",
          quantity: providerResidentUpgradeDelta,
          unitPrice: BillingConstants.SUBSCRIPTION_PRICE_BY_TYPE[BillingConstants.SUBSCRIPTION_TYPE.ADD_RESIDENT],
          lineTotal: providerResidentUpgradeDelta * BillingConstants.SUBSCRIPTION_PRICE_BY_TYPE[BillingConstants.SUBSCRIPTION_TYPE.ADD_RESIDENT],
        }] : []),
        ...(providerCaregiverBlockUpgradeDelta > 0 ? [{
          key: "provider-staff-plan-change",
          name: "Staff Plan Change",
          quantity: providerCaregiverBlockUpgradeDelta,
          unitPrice: BillingConstants.SUBSCRIPTION_PRICE_BY_TYPE[BillingConstants.SUBSCRIPTION_TYPE.ADD_CAREGIVER],
          lineTotal: providerCaregiverBlockUpgradeDelta * BillingConstants.SUBSCRIPTION_PRICE_BY_TYPE[BillingConstants.SUBSCRIPTION_TYPE.ADD_CAREGIVER],
        }] : []),
      ]
      : selectedSubscriptionServices
        .map((service: any, idx: number) => {
          const subscriptionType = selectedSubscriptionServiceTypes[idx];
          const quantity = subscriptionType ? (planQuantities[subscriptionType] || 0) : 0;
          const unitPrice = subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME
            ? providerSingleHomeSubscriptionPrice
            : subscriptionType ? (BillingConstants.SUBSCRIPTION_PRICE_BY_TYPE[subscriptionType] || 0) : 0;

          if (!subscriptionType || quantity <= 0) {
            return null;
          }

          return {
            key: `${service.name}-${idx}`,
            name: service.name,
            quantity,
            unitPrice,
            lineTotal: quantity * unitPrice,
          };
        })
        .filter(Boolean);
  const hideSubscribedPlanAccordion = isUserSubscribed
    && (isNurseBillingRole || isFixedPriceNoBundleBillingRole);
  const visibleSubscribedSummaryServices = hideSubscribedPlanAccordion
    ? []
    : selectedSubscriptionServices
      .map((service: any, idx: number) => ({
        service,
        subscriptionType: selectedSubscriptionServiceTypes[idx],
        idx,
      }))
      .filter((item: any) => !!item.subscriptionType);

  const existingPrice = isUserSubscribed
    ? isNursePlanCart
      ? currentNurseSubscriptionAmount
      : resolvedSubscriptionRoleType === ROLE_TYPE_PROVIDER
        ? providerRecurringSubscriptionAmount
        : complementaryRecurringAmount
    : complementaryRecurringAmount;
  const newItemsSubtotal = isUserSubscribed
    ? isNursePlanCart
      ? nurseSubscriptionDeltaAmount
      : resolvedSubscriptionRoleType === ROLE_TYPE_PROVIDER
        ? providerSubscriptionDeltaAmount
        : Number(totalPrice || 0)
    : Number(totalPrice || 0);
  const updatedRecurringPrice = isUserSubscribed
    ? existingPrice + newItemsSubtotal
    : Number(totalPrice || 0);
  const subscriptionTaxableAmount = existingPrice + newItemsSubtotal;
  const immediateSubscriptionTaxableAmount = Math.max(0, newItemsSubtotal);
  const taxAmount = Number(taxDetails?.tax || 0);
  const totalWithTax = taxDetails
    ? Number(taxDetails.total || 0)
    : (isImmediateSubscriptionUpdate ? immediateSubscriptionTaxableAmount : subscriptionTaxableAmount);
  const existingPriceLabel = resolvedSubscriptionRoleType === ROLE_TYPE_PROVIDER
    ? "Current Subscription"
    : isNursePlanCart
      ? "Current Subscription"
      : "Current Hub Plan";
  const updatedRecurringPriceLabel = "New Subscription Price";
  const newItemsSubtotalLabel = isImmediateSubscriptionUpdate
    ? "Plan Changes"
    : isNonProratedSubscriptionUpdate
      ? (isReplacementSubscriptionUpdate ? "Selected Plan" : "Plan Changes")
      : "New Items Subtotal";
  const totalPriceLabel = isImmediateSubscriptionUpdate
    ? (taxDetails ? "Total Due Today (Incl. Tax)" : "Total Due Today Before Tax")
    : isNonProratedSubscriptionUpdate
      ? (taxDetails ? "New Monthly Total (Incl. Tax)" : "New Monthly Total Before Tax")
      : (taxDetails ? "Total (Incl. Tax)" : "Total Before Tax");
  const subscriptionCartHelperText = trialEndDate
    ? ""
    : subscriptionCancelDate
      ? ""
      : isImmediateSubscriptionUpdate
        ? "This update charges only the added subscription difference today."
        : isNonProratedSubscriptionUpdate
          ? "This update will apply on your next renewal. You will not be charged today."
          : taxDetails
            ? "Review your updated total before continuing to payment."
            : "Tax will be calculated after you continue to payment.";
  const selectedBundle = getBundleOption(selectedBundlePurchaseType);
  const selectedBundleQuantity = getBundleQuantity(selectedBundlePurchaseType);
  const getInvoiceStatusMeta = (status?: string | null) => {
    const normalizedStatus = String(status || "").toLowerCase();

    if (normalizedStatus === "paid") {
      return {
        accent: "#166534",
        muted: "#15803d",
        border: "#86efac",
        background: "#f0fdf4",
      };
    }

    if (["open", "uncollectible"].includes(normalizedStatus)) {
      return {
        accent: "#9a3412",
        muted: "#c2410c",
        border: "#fdba74",
        background: "#fff7ed",
      };
    }

    if (["void", "draft"].includes(normalizedStatus)) {
      return {
        accent: "#475569",
        muted: "#64748b",
        border: "#cbd5e1",
        background: "#f8fafc",
      };
    }

    return {
      accent: "#0f172a",
      muted: "#64748b",
      border: "#e2e8f0",
      background: "#ffffff",
    };
  };
  const getOneTimePurchaseStatusMeta = () => ({
    accent: "#166534",
    muted: "#15803d",
    border: "#86efac",
    background: "#f0fdf4",
  });
  const paymentMethodAttentionMeta = (() => {
    const normalizedStatus = String(subscriptionStatus || "").toLowerCase();
    if (normalizedStatus === "past_due") {
      return {
        title: "Payment method attention needed",
        message: "Your latest subscription payment did not go through. Please update or confirm your payment method so we can retry billing.",
        accent: "#9a3412",
        background: "#fff7ed",
        border: "#fdba74",
      };
    }
    if (normalizedStatus === "unpaid") {
      return {
        title: "Subscription payment is unpaid",
        message: "Your subscription has an unpaid balance. Please update your payment method to restore billing.",
        accent: "#991b1b",
        background: "#fef2f2",
        border: "#fca5a5",
      };
    }
    if (normalizedStatus === "incomplete") {
      return {
        title: "Finish your subscription setup",
        message: "Your first subscription payment was not completed. Please update or confirm your payment method to finish setup.",
        accent: "#9a3412",
        background: "#fff7ed",
        border: "#fdba74",
      };
    }
    if (normalizedStatus === "incomplete_expired") {
      return {
        title: "Subscription setup expired",
        message: "Your original subscription setup was not completed in time. Add or update a payment method and start checkout again.",
        accent: "#991b1b",
        background: "#fef2f2",
        border: "#fca5a5",
      };
    }
    if (normalizedStatus === "paused") {
      return {
        title: "Add a payment method to resume",
        message: "Your subscription is paused because Stripe needs a valid default payment method before billing can continue.",
        accent: "#185DA2",
        background: "#eff6ff",
        border: "#93c5fd",
      };
    }
    return null;
  })();
  const outstandingSubscriptionInvoices = invoiceHistory.filter((invoice) => {
    const normalizedStatus = String(invoice?.status || "").toLowerCase();
    const amountDue = Number(invoice?.amountDue || 0);
    if (normalizedStatus === "paid") {
      return false;
    }
    return amountDue > 0 || ["open", "uncollectible"].includes(normalizedStatus);
  });
  const renderPaymentAttentionBanner = () => {
    if (!isUserSubscribed) {
      return null;
    }

    if (!paymentMethodAttentionMeta && billingRecoveryStatus === "healthy") {
      return null;
    }

    return (
      <div className="mt-3" style={{ background: "#fff7ed", border: "1px solid #fdba74", borderRadius: "12px", padding: "0.85rem 1rem", color: "#9a3412" }}>
        <div style={{
          background: paymentMethodAttentionMeta?.background || "#fff7ed",
          border: `1px solid ${paymentMethodAttentionMeta?.border || "#fdba74"}`,
          borderRadius: "12px",
          padding: "0.85rem 1rem",
          color: paymentMethodAttentionMeta?.accent || "#9a3412"
        }}>
          <div className="flex flex-wrap align-items-start justify-content-between gap-3">
            <div>
              <div style={{ fontWeight: 700 }}>
                {paymentMethodAttentionMeta?.title || "Billing attention needed"}
              </div>
              <div className="mt-2">
                {paymentMethodAttentionMeta?.message || lastPaymentFailureReason || "A recent billing attempt needs your attention."}
              </div>
              {subscriptionStatus && paymentMethodAttentionMeta && (
                <div className="mt-2" style={{ fontSize: "0.92rem", textTransform: "capitalize" }}>
                  Current Stripe status: {subscriptionStatus.replace(/_/g, " ")}
                </div>
              )}
              {!paymentMethodAttentionMeta && lastPaymentFailureAt && (
                <div className="mt-2">Last failed attempt: {lastPaymentFailureAt.toLocaleDateString()}</div>
              )}
              {nextRetryAt && (
                <div className="mt-1">Next retry: {nextRetryAt.toLocaleDateString()}</div>
              )}
            </div>
            {paymentMethodAttentionMeta && (
              <div className="flex justify-content-center align-items-center">
                <Button
                  label={paymentMethodSummary ? "Update Payment Method" : "Add Payment Method"}
                  severity="warning"
                  rounded
                  className="p-button-sm"
                  onClick={() => onUpdateCardSubmit(0)}
                  loading={loadingPaymentIntent && paymentMode === "cardUpdate"}
                  data-telemetry-action="billing_attention_update_payment_method"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  const renderOutstandingPaymentsCard = () => {
    if (!["past_due", "unpaid"].includes(String(subscriptionStatus || "").toLowerCase()) || outstandingSubscriptionInvoices.length === 0) {
      return null;
    }

    return (
      <Card
        className="mt-3"
        style={{ ...sectionCardStyle, padding: "0.5rem", border: "1px solid #fecaca", background: "linear-gradient(135deg, #fff7f7 0%, #ffffff 100%)" }}
        header={cardTitleTemplate("Outstanding Payments")}
      >
        <div style={{ color: "#7f1d1d", lineHeight: 1.7, marginBottom: "1rem" }}>
          Review the subscription invoices that still need to be cleared. Once payment succeeds, Stripe can move your subscription back into good standing.
        </div>
        <div className="flex flex-column gap-3">
          {outstandingSubscriptionInvoices.map((invoice) => (
            <div key={invoice.invoiceId} style={{ background: "#ffffff", border: "1px solid #fecaca", borderRadius: "12px", padding: "0.9rem 1rem" }}>
              <div className="flex flex-wrap justify-content-between gap-3">
                <div>
                  <div style={{ fontWeight: 700, color: "#7f1d1d" }}>
                    {invoice.description || "Past-due subscription invoice"}
                  </div>
                  <div className="mt-1" style={{ color: "#7f1d1d" }}>
                    {formatHistoryDate(invoice.createdAt)} · {String(invoice.status || "").replace(/_/g, " ")}
                  </div>
                  {nextRetryAt && (
                    <div className="mt-1" style={{ color: "#991b1b" }}>
                      Next retry: {nextRetryAt.toLocaleDateString()}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 700, color: "#7f1d1d" }}>
                    ${Number(invoice.amountDue ?? invoice.amountPaid ?? 0).toFixed(2)}
                  </div>
                  <div className="mt-1" style={{ color: "#991b1b" }}>
                    {invoice.currency ? String(invoice.currency).toUpperCase() : ""}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap justify-content-center gap-2">
                {invoice.hostedInvoiceUrl || invoice.invoicePdf || invoice.receiptUrl ? (
                  <Button
                    type="button"
                    label="View Invoice"
                    severity="secondary"
                    text
                    rounded
                    className="p-button-sm"
                    style={{ width: "auto" }}
                    onClick={() => openExternalUrl(invoice.hostedInvoiceUrl || invoice.invoicePdf || invoice.receiptUrl)}
                    data-telemetry-action="billing_view_outstanding_invoice"
                  />
                ) : null}
                {canRetryPayment ? (
                  <Button
                    type="button"
                    label="Retry Payment"
                    severity="warning"
                    rounded
                    outlined
                    className="p-button-sm"
                    style={{ width: "auto" }}
                    onClick={onRetryPaymentSubmit}
                    loading={loadingPaymentIntent}
                    data-telemetry-action="billing_retry_outstanding_payment"
                  />
                ) : null}
                <Button
                  type="button"
                  label={paymentMethodSummary ? "Update Payment Method" : "Add Payment Method"}
                  severity="warning"
                  rounded
                  className="p-button-sm"
                  style={{ width: "auto" }}
                  onClick={() => onUpdateCardSubmit(0)}
                  loading={loadingPaymentIntent && paymentMode === "cardUpdate"}
                  data-telemetry-action="billing_outstanding_update_payment_method"
                />
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  };
  const paymentCheckoutReturnUrl =
    paymentMode === "bundle"
      ? `${window.location.origin}${RouterAddress.BILLING}?purchase=bundle&bundleType=${encodeURIComponent(selectedBundlePurchaseType)}&quantity=${selectedBundleQuantity}`
      : `${window.location.origin}${RouterAddress.BILLING}?flow=${paymentMode === "cardUpdate" ? "cardUpdate" : "subscription"}${paymentMode === "subscription" ? `&chargeMode=${encodeURIComponent(subscriptionCheckoutKind)}` : ""}`;
  const totalFeatureCreditsAvailable = visibleFeatureCreditPurchaseOptions.reduce(
    (sum, item) => sum + getFeatureCreditsFromBalances(item.featureType, featureCreditBalances),
    0,
  );
  const planComparisonConfig = activeBillingCatalog?.planComparison || sharedPlanComparisonConfigByRole[activeBillingRoleType] || sharedPlanComparisonConfigByRole[ROLE_TYPE_PROVIDER];
  const hasAnyOneTimeBalanceAvailable =
    remainingHomes > 0
    || remainingResidents > 0
    || websitePublishCreditsAvailable > 0
    || websitePublishUnlockedHomeIds.length > 0
    || totalFeatureCreditsAvailable > 0;

  useEffect(() => {
    if (hasAnyOneTimeBalanceAvailable) {
      setOneTimeBalancesExpanded(true);
    }
  }, [hasAnyOneTimeBalanceAvailable]);

  const isOneTimeCheckoutMode = paymentMode === "bundle";
  const oneTimeCheckoutDetails = paymentMode === "bundle"
    ? {
      title: `Buy ${selectedBundle?.name || "Purchase"}`,
      helperText: "Add your card once and this purchase will be added to your account right away.",
      lineItems: [
        {
          label: `${selectedBundleQuantity} ${(selectedBundle?.name || "Purchase")}${selectedBundleQuantity > 1 ? "s" : ""}`,
          amount: Number(selectedBundle?.price || 0) * selectedBundleQuantity,
        },
      ],
      taxAmount: Number(bundleTaxDetails?.tax || 0),
      totalAmount: bundleTaxDetails ? Number(bundleTaxDetails.total || 0) : (Number(selectedBundle?.price || 0) * selectedBundleQuantity),
      submitLabel: `Pay $${(bundleTaxDetails ? Number(bundleTaxDetails.total || 0) : (Number(selectedBundle?.price || 0) * selectedBundleQuantity)).toFixed(2)}`,
    }
    : null;
  const oneTimeBuyTabHeader = oneTimeTabConfig.tabHeader;
  const automationSummaryContent = (
    <div
      style={{
        background: "linear-gradient(135deg, #fff7ed 0%, #ffffff 100%)",
        border: "1px solid #fdba74",
        borderRadius: "18px",
        padding: "1rem 1.05rem",
        boxShadow: "0 14px 34px rgba(245, 158, 11, 0.10)",
      }}
    >
      <div style={{ color: "#9a3412", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>
        Automation Credits
      </div>
      <div className="mt-2" style={{ color: "#0f172a", fontSize: "1.9rem", fontWeight: 700 }}>
        {totalFeatureCreditsAvailable}
      </div>
      <div className="mt-1" style={{ color: "#7c2d12", fontWeight: 700 }}>
        {`You have ${totalFeatureCreditsAvailable} credit${totalFeatureCreditsAvailable === 1 ? "" : "s"} available.`}
      </div>
      <div className="mt-3" style={{ color: "#596F80", lineHeight: 1.6 }}>
        Use this tab to review available credits and buy more when you need extra automation runs.
      </div>
    </div>
  );
  const automationCreditUsageContent = (
    <div
      style={{
        background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
        border: "1px solid #cbd5e1",
        borderRadius: "18px",
        padding: "1rem 1.05rem",
        boxShadow: "0 14px 34px rgba(15, 23, 42, 0.06)",
      }}
    >
      <div style={{ color: "#334155", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>
        Credit usage
      </div>
      <div className="mt-2" style={{ color: "#0f172a", fontSize: "1.15rem", fontWeight: 700 }}>
        What automation credits are used for
      </div>
      <div className="mt-2" style={{ color: "#596F80", lineHeight: 1.6 }}>
        One eligible automation job uses one credit when included subscription usage is unavailable.
      </div>
      <ul className="mt-3" style={{ margin: 0, paddingLeft: "1.15rem", color: "#334155", lineHeight: 1.7 }}>
        <li>Assessment-to-NCP and next-assessment NCP drafts</li>
        <li>Provider and nurse resident assessment drafts</li>
        <li>Nurse NCP drafts</li>
        <li>Nurse delegation form drafts</li>
        <li>Foot and nail care form generation</li>
      </ul>
    </div>
  );
  const formatHistoryDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString() : "-";
  /*
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr)",
        gap: "1rem",
        width: "100%",
        maxWidth: "720px",
        margin: "0 auto",
      }}
    >
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #dbeafe",
          borderRadius: "18px",
          padding: "1.1rem 1.15rem",
        }}
      >
        <div className="flex flex-wrap align-items-start justify-content-between gap-3">
          <div style={{ maxWidth: "42rem" }}>
            <div className="flex flex-wrap align-items-center gap-2">
              <div style={{ fontSize: "0.78rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>
                Nurse Plan Add-On
              </div>
              <Button
                type="button"
                label={nurseAddOnEnabled ? "Enabled" : "Not Enabled"}
                severity={nurseAddOnEnabled ? "success" : "secondary"}
                rounded
                outlined
                size="small"
                disabled
                style={{ width: "auto", pointerEvents: "none" }}
              />
            </div>
            <div className="mt-2" style={{ fontSize: "1.1rem", fontWeight: 700, color: "#0f172a" }}>
              Unlock provider nurse workspace access.
            </div>
            <div className="mt-3" style={{ color: "#475569", lineHeight: 1.75 }}>
              As a Registered Nurse, you can perform Resident Assessment and Delegation workflows.
            </div>
            <div className="mt-2" style={{ color: "#9a3412", fontWeight: 700, lineHeight: 1.75 }}>
              Automation credits generate the assessment and delegation forms for you.
            </div>
            <div className="mt-2" style={{ color: "#475569", lineHeight: 1.75 }}>
              You can open the Nurse Dashboard from the header and switch between Provider and Nurse workspaces there.
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap align-items-center gap-3">
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "14px",
              padding: "0.85rem 1rem",
              minWidth: "190px",
            }}
          >
            <div style={{ color: "#64748b", fontSize: "0.82rem" }}>Monthly charge</div>
            <div className="mt-1" style={{ color: "#0f172a", fontWeight: 700 }}>$0</div>
          </div>
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "14px",
              padding: "0.85rem 1rem",
              minWidth: "220px",
            }}
          >
            <div style={{ color: "#64748b", fontSize: "0.82rem" }}>Automation credits</div>
            <div className="mt-1" style={{ color: "#0f172a", fontWeight: 700 }}>
              Optional — buy credits to run automated drafts
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {!nurseAddOnEnabled ? (
            <Button
              type="button"
              label="Enable Add-On"
              severity="warning"
              rounded
              style={{ width: "auto" }}
              onClick={() => {
                setNurseAddOnCreditPackCount(1);
                setNurseAddOnDialogVisible(true);
              }}
            />
          ) : (
            <>
              <Button
                type="button"
                label="Buy Automation Credits"
                severity="warning"
                rounded
                outlined
                style={{ width: "auto" }}
                onClick={() => onBillingTabChange(1)}
              />
              <Button
                type="button"
                label="Remove Add-On"
                severity="secondary"
                rounded
                outlined
                style={{ width: "auto" }}
                loading={loadingPaymentIntent}
                onClick={() => {
                  confirmDialog({
                    header: "Remove Nurse Plan Add-On",
                    message: "This will hide provider nurse workspace access until you enable the add-on again.",
                    icon: "pi pi-exclamation-triangle",
                    acceptClassName: "p-button-warning p-button-rounded",
                    rejectClassName: "p-button-text",
                    acceptLabel: "Remove Add-On",
                    rejectLabel: "Keep Add-On",
                    accept: onDisableNurseAddOnSubmit,
                  });
                }}
              />
            </>
          )}
        </div>
      </div>
      <Dialog
        header="Enable Nurse Plan Add-On"
        visible={nurseAddOnDialogVisible}
        style={{ width: "92vw", maxWidth: "580px" }}
        onHide={() => setNurseAddOnDialogVisible(false)}
      >
        <div style={{ display: "grid", gap: "1rem", lineHeight: 1.7 }}>
          <div style={{ color: "#475569" }}>
            Enable the add-on to unlock the Nurse Dashboard and switch workspaces from the header.
          </div>
          <div style={{ color: "#9a3412", fontWeight: 700 }}>
            Automation credits generate the assessment and delegation forms for you. 1 automation job = 1 credit.
          </div>
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "14px",
              padding: "1rem",
            }}
          >
            <div style={{ color: "#64748b", fontSize: "0.82rem" }}>Available automation credits</div>
            <div className="mt-1" style={{ color: "#0f172a", fontWeight: 700 }}>
              {purchasedAutomationCreditsAvailable}
            </div>
            <div className="mt-2" style={{ color: "#64748b", fontSize: "0.9rem" }}>
              {purchasedAutomationCreditsAvailable > 0
                ? `You have ${purchasedAutomationCreditsAvailable} automation credit${purchasedAutomationCreditsAvailable === 1 ? "" : "s"} available.`
                : "You can enable the add-on now. Buy credits anytime to run automated drafts."}
            </div>
            <div className="mt-1" style={{ color: "#0f172a", fontWeight: 700 }}>
              {BillingConstants.PROVIDER_NURSE_AUTOMATION_PACK_SIZE} credits: ${Number(automationCreditBundle?.price || BillingConstants.PROVIDER_NURSE_AUTOMATION_PACK_PRICE).toFixed(2)}
            </div>
          </div>
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              text
              rounded
              onClick={() => setNurseAddOnDialogVisible(false)}
            />
            <Button
              type="button"
              label="Enable"
              severity="secondary"
              rounded
              outlined
              loading={loadingPaymentIntent}
              onClick={() => {
                void onEnableNurseAddOnOnlySubmit();
              }}
            />
            <Button
              type="button"
              label={`Add ${BillingConstants.PROVIDER_NURSE_AUTOMATION_PACK_SIZE} Credits`}
              severity="warning"
              rounded
              loading={loadingPaymentIntent}
              onClick={() => {
                setNurseAddOnCreditPackCount(1);
                void onEnableNurseAddOnSubmit();
              }}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );

  */

  const billingToolsItems = [
    {
      label: "Payment History",
      icon: "pi pi-history",
      command: () => {
        setPaymentHistoryVisible(true);
        setBillingToolsVisible(false);
      },
    },
    {
      label: "Update Billing Address",
      icon: "pi pi-map-marker",
      command: () => {
        openBillingAddressDialog();
        setBillingToolsVisible(false);
      },
    },
    {
      label: "Update Payment Method",
      icon: "pi pi-credit-card",
      command: () => {
        setPaymentDetailsVisible(true);
        setBillingToolsVisible(false);
      },
    },
    canRetryPayment ? {
      label: "Retry Payment",
      icon: "pi pi-refresh",
      command: () => {
        onRetryPaymentSubmit();
        setBillingToolsVisible(false);
      },
    } : null,
    canRemoveOnePlan ? {
      label: "Cancel Nurse Plan",
      icon: "pi pi-minus-circle",
      command: () => {
        setBillingToolsVisible(false);
        confirmDialog({
          header: "Cancel Nurse Plan",
          message: "This will remove the nurse portion of your subscription and keep your hub plan active. The updated monthly total will take effect on your next renewal. Do you want to continue?",
          icon: "pi pi-exclamation-triangle",
          acceptClassName: "p-button-warning p-button-rounded",
          rejectClassName: "p-button-text",
          acceptLabel: "Cancel Nurse Plan",
          rejectLabel: "Keep Both Plans",
          accept: () => onRemoveSubscriptionPlanSubmit("nurse"),
        });
      },
    } : null,
    canRemoveOnePlan ? {
      label: "Cancel Hub Plan",
      icon: "pi pi-minus-circle",
      command: () => {
        setBillingToolsVisible(false);
        confirmDialog({
          header: "Cancel Hub Plan",
          message: "This will remove the hub portion of your subscription. Provider dashboard capacity will be reduced to your one-time balances, and the updated monthly total will take effect on your next renewal. Do you want to continue?",
          icon: "pi pi-exclamation-triangle",
          acceptClassName: "p-button-warning p-button-rounded",
          rejectClassName: "p-button-text",
          acceptLabel: "Cancel Hub Plan",
          rejectLabel: "Keep Plan",
          accept: () => onRemoveSubscriptionPlanSubmit("provider"),
        });
      },
    } : null,
    !cancelAtPeriodEnd && isUserSubscribed ? {
      label: "Cancel Subscription",
      icon: "pi pi-calendar-times",
      command: () => {
        setBillingToolsVisible(false);
        openCancelReasonDialog("end_of_period");
      },
    } : null,
    cancelAtPeriodEnd ? {
      label: "Resume Subscription",
      icon: "pi pi-replay",
      command: () => {
        setBillingToolsVisible(false);
        confirmDialog({
          header: "Resume Subscription",
          message: "This will remove the scheduled cancellation and keep your subscription active. Do you want to continue?",
          icon: "pi pi-refresh",
          acceptClassName: "p-button-info p-button-rounded billing-resume-subscription-accept",
          rejectClassName: "p-button-text",
          acceptLabel: "Resume Subscription",
          rejectLabel: "Keep Scheduled Cancel",
          accept: onResumeSubscriptionSubmit,
        });
      },
    } : null,
  ].filter(Boolean);

  const onBillingAddressElementChange = (payload: { complete: boolean; address: any; }) => {
    const nextDraft = normalizeBillingAddress(payload.address);
    setBillingAddressDraft(nextDraft);
    setIsBillingAddressElementComplete(!!payload.complete);
    setBillingAddressErrors((prev) => {
      const nextErrors = { ...prev };
      delete nextErrors.addressElement;
      return nextErrors;
    });
  };

  const saveBillingAddress = async (postSaveAction?: (normalizedBillingAddress: any) => Promise<void> | void) => {
    const { normalized, nextErrors } = validateBillingAddressDraft(billingAddressDraft);
    if (Object.keys(nextErrors).length > 0) {
      setBillingAddressErrors(nextErrors);
      return;
    }

    setBillingAddressSaving(true);
    try {
      await verifyBillingAddressTaxResponse(normalized);
      await syncBillingAddressToUserProfile(normalized);
      setBillingAddress(normalized);
      setBillingAddressDraft(normalized);
      setIsBillingAddressElementComplete(true);
      setTaxDetails(null);
      setBundleTaxDetails(null);
      const pendingAction = pendingBillingAddressActionRef.current;
      setBillingAddressDialogVisible(false);

      if (postSaveAction) {
        await postSaveAction(normalized);
      } else if (pendingAction) {
        await pendingAction(normalized);
      } else {
        setTaxDetails(null);
      }
      pendingBillingAddressActionRef.current = null;
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Unable to save billing address",
        detail: "Please review the address and try again.",
        life: 3500,
      });
      setBillingAddressDialogVisible(true);
    } finally {
      setBillingAddressSaving(false);
    }
  };

  const onSaveBillingAddress = async () => {
    await saveBillingAddress();
  };

  const onSaveBillingAddressAndUpdatePaymentMethod = async () => {
    const shouldOpenSubscriptionWizard = !isUserSubscribed && !paymentMethodSummary;
    await saveBillingAddress(async (normalizedBillingAddress) => {
      if (shouldOpenSubscriptionWizard) {
        // Hide the page-level checkout form before creating the setup intent so
        // the payment details are mounted only inside the subscription wizard.
        setSubscriptionWizardDialogVisible(true);
        setSubscriptionWizardStep(0);
        await onProceedToPaymentSubmit(normalizedBillingAddress);
        setBillingAddressDialogVisible(true);
        return;
      }
      await onUpdateCardSubmit(billingTabIndex > 0 ? billingTabIndex : 0, normalizedBillingAddress);
    });
  };

  const onBillingTabChange = (index: number) => {
    if (useSinglePlanBillingView) {
      return;
    }
    setBillingTabIndex(index);

    const params = new URLSearchParams(location.search);
    params.set("tab", index === 2 ? "nurse-addon" : index === 1 ? "credits" : "subscription");
    params.delete("billingTab");

    const nextSearch = params.toString();
    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}${location.hash || ""}`, { replace: true });
  };

  const billingSegmentButtonStyle = (active: boolean) => ({
    border: "none",
    borderRadius: "999px",
    padding: "0.45rem 0.9rem",
    fontSize: "0.82rem",
    fontWeight: 700,
    lineHeight: 1,
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: active ? "#f59e0b" : "transparent",
    color: active ? "#ffffff" : "#64748b",
    boxShadow: active ? "0 8px 18px rgba(245, 158, 11, 0.28)" : "none",
  });

  return (
    <div className="container-fluid p-3">
      <Toast ref={toast} />
      <Dialog
        visible={cancelDialogVisible}
        onHide={() => {
          if (loadingPaymentIntent) return;
          setCancelDialogVisible(false);
          setCancelDialogReason("");
          setCancelDialogOtherReason("");
          setCancelDialogError("");
        }}
        header="Cancel Subscription at End of Billing Period"
        style={{ width: "92vw", maxWidth: "34rem" }}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        footer={
          <div style={appDialogFooterStyle}>
            <Button
              type="button"
              label="Keep Subscription"
              severity="secondary"
              text
              onClick={() => {
                setCancelDialogVisible(false);
                setCancelDialogReason("");
                setCancelDialogOtherReason("");
                setCancelDialogError("");
              }}
              disabled={loadingPaymentIntent}
            />
            <Button
              type="button"
              label="Schedule Cancellation"
              severity="warning"
              rounded
              onClick={() => {
                void onCancelSubscriptionSubmit();
              }}
              loading={loadingPaymentIntent}
            />
          </div>
        }
      >
        <div style={{ color: "#475569", lineHeight: 1.7 }}>
          Your subscription will stay active until the current billing period ends.{" "}
          Please tell us why you're canceling.
        </div>
        <div className="mt-3">
          <label htmlFor="billing-cancel-reason" style={{ display: "block", fontWeight: 600, color: "#0f172a", marginBottom: "0.5rem" }}>
            Cancellation reason
          </label>
          <Dropdown
            id="billing-cancel-reason"
            value={cancelDialogReason}
            options={CANCELLATION_REASON_OPTIONS}
            optionLabel="label"
            optionValue="value"
            placeholder="Select a reason"
            className={cancelDialogError ? "p-invalid" : ""}
            onChange={(event) => {
              setCancelDialogReason(event.value || "");
              setCancelDialogOtherReason("");
              if (cancelDialogError) {
                setCancelDialogError("");
              }
            }}
            style={{ width: "100%" }}
          />
          <InputTextarea
            id="billing-cancel-reason-other"
            value={cancelDialogOtherReason}
            onChange={(event) => {
              setCancelDialogOtherReason(event.target.value);
              if (cancelDialogError) {
                setCancelDialogError("");
              }
            }}
            rows={4}
            autoResize
            className={cancelDialogError && cancelDialogReason === "Other" ? "p-invalid" : ""}
            placeholder={cancelDialogReason === "Other" ? "Please share your reason" : "Additional details (optional)"}
            style={{ width: "100%", marginTop: "0.75rem" }}
          />
          {cancelDialogError && (
            <small style={{ color: "#dc2626", display: "block", marginTop: "0.5rem" }}>
              {cancelDialogError}
            </small>
          )}
        </div>
      </Dialog>
      <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
        <div className="mb-4">
          <div>
            <div className="flex flex-wrap align-items-center justify-content-between gap-3">
              <h2 className="m-0">Billing & Payments</h2>
              <div
                ref={billingToolsMenuRef}
                className="flex justify-content-end align-items-center flex-wrap"
                style={{ position: "relative", gap: "0.75rem" }}
              >
                <Tag
                  value={
                    isFacilityWorkerBillingRole
                      ? "Free Access"
                      : isUserSubscribed
                      ? (trialEndDate ? "Free Trial" : cancelAtPeriodEnd ? "Cancel Scheduled" : "Active")
                      : "No Active Subscription"
                  }
                  severity={isFacilityWorkerBillingRole ? "success" : isUserSubscribed ? (cancelAtPeriodEnd ? "warning" : "success") : "danger"}
                  rounded
                />
                {!isFacilityWorkerBillingRole && hasCompleteBillingAddress(billingAddress) && paymentMethodSummary?.id ? (
                  <Button
                    type="button"
                    label="Manage Billing"
                    icon="pi pi-angle-down"
                    iconPos="right"
                    severity="warning"
                    outlined
                    rounded
                    onClick={() => setBillingToolsVisible((prev) => !prev)}
                  />
                ) : null}
                {billingToolsVisible && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 0.5rem)",
                      right: 0,
                      minWidth: "240px",
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "14px",
                      boxShadow: "0 18px 38px rgba(15, 23, 42, 0.16)",
                      padding: "0.45rem",
                      zIndex: 1000,
                    }}
                  >
                    <div className="flex flex-column">
                      {(billingToolsItems as any[]).map((item) => (
                        <button
                          key={item.label}
                          type="button"
                          onClick={item.command}
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.7rem",
                            padding: "0.8rem 0.9rem",
                            borderRadius: "10px",
                            border: "none",
                            background: "transparent",
                            color: "#0f172a",
                            cursor: "pointer",
                            textAlign: "left",
                          }}
                        >
                          <i className={item.icon} style={{ color: "#9a3412", width: "1rem" }} />
                          <span>{item.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {paymentReturnMessage && (
              <div className="mt-3" style={{ background: "#ecfdf3", border: "1px solid #86efac", color: "#166534", borderRadius: "14px", padding: "0.85rem 1rem" }}>
                {paymentReturnMessage}
              </div>
            )}
          </div>
        </div>
        <Card
          className="p-fluid"
          style={sectionCardStyle}
          pt={{
            body: { style: { paddingTop: "1em" } },
            content: { style: { paddingTop: "1em" } },
          }}
        >
          {
            loading ? (
              <div className="col-12 text-center d-flex justify-content-center align-items-center">
                <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
              </div>
            ) : isFacilityWorkerBillingRole ? (
              <div
                style={{
                  background: "linear-gradient(135deg, #f0fdf4 0%, #ffffff 72%)",
                  border: "1px solid #86efac",
                  borderRadius: "18px",
                  padding: "1.25rem",
                }}
              >
                <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: "#166534", fontSize: "0.8rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>Facility Worker</div>
                    <h3 className="mt-2 mb-1" style={{ color: "#0f172a" }}>{facilityWorkerBillingService.name}</h3>
                    <div style={{ color: "#475569", lineHeight: 1.65 }}>Your role has free access to assigned facility work. No subscription checkout, billing address, or payment method is required.</div>
                  </div>
                  <div className="flex flex-column align-items-end gap-2">
                    <Tag value="Free Access" severity="success" rounded />
                    <div style={{ color: "#166534", fontSize: "1.35rem", fontWeight: 800 }}>$0/month</div>
                  </div>
                </div>
                <div className="mt-4" style={{ background: "#ffffff", border: "1px solid #bbf7d0", borderRadius: "14px", padding: "1rem" }}>
                  <div style={{ color: "#0f172a", fontWeight: 800 }}>Included with your role</div>
                  <ul className="mb-0 mt-2 pl-4" style={{ color: "#475569", lineHeight: 1.8 }}>
                    {facilityWorkerBillingService.includes.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              </div>
            ) : (
              <>
                {processingReturn && (
                  <div className="mb-4" style={{ background: "#eff6ff", border: "1px solid #bfdbfe", color: "#185DA2", borderRadius: "14px", padding: "1rem" }}>
                    <div className="flex align-items-center gap-3">
                      <ProgressSpinner style={{ width: "24px", height: "24px" }} strokeWidth="6" />
                      <div>Finalizing your billing update...</div>
                    </div>
                  </div>
                )}
                <div style={{ position: "relative" }}>
                  {!useSinglePlanBillingView && isUserSubscribed && (
                    <div className="mb-4 flex justify-content-center">
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.2rem",
                          padding: "0.2rem",
                          borderRadius: "999px",
                          border: "1px solid #e2e8f0",
                          background: "#f8fafc",
                          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
                          flexWrap: "nowrap",
                        }}
                        aria-label="Billing section switcher"
                      >
                        <button
                          type="button"
                          aria-pressed={billingTabIndex === 0}
                          onClick={() => onBillingTabChange(0)}
                          style={billingSegmentButtonStyle(billingTabIndex === 0)}
                        >
                          Subscription
                        </button>
                        <button
                          type="button"
                          aria-pressed={billingTabIndex === 1}
                          onClick={() => onBillingTabChange(1)}
                          style={billingSegmentButtonStyle(billingTabIndex === 1)}
                        >
                          {oneTimeBuyTabHeader}
                        </button>
                      </div>
                    </div>
                  )}
                  {useSinglePlanBillingView ? (
                    <BillingSubscriptionTabContent
                      isUserSubscribed={isUserSubscribed}
                      renderPaymentAttentionBanner={renderPaymentAttentionBanner}
                      renderOutstandingPaymentsCard={renderOutstandingPaymentsCard}
                      subscriptionSummaryContent={(
                        <div>
                          {isUserSubscribed ? (
                            <>
                              {isNurseBillingRole ? (
                                <>
                                  {cancelAtPeriodEnd && subscriptionCancelDate && (
                                    <div className="mt-1" style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "12px", padding: "0.85rem 1rem", color: "#185DA2" }}>
                                      Subscription will cancel on {subscriptionCancelDate.toLocaleDateString()} unless you resume it before then.
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="grid align-items-start">
                                  <div className="col-12">
                                    <div
                                      style={{
                                        background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 65%)",
                                        borderRadius: "18px",
                                        border: "1px solid #bfdbfe",
                                        padding: "1.1rem 1.15rem",
                                      }}
                                    >
                                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap" }}>
                                        <div>
                                          <div style={{ color: "#185DA2", fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>
                                            Subscription
                                          </div>
                                        </div>
                                      </div>
                                      <div
                                        className="mt-3"
                                        style={{
                                          background: "#ffffff",
                                          border: "1px solid #dbeafe",
                                          borderRadius: "14px",
                                          padding: "0.85rem 0.95rem",
                                        }}
                                      >
                                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>Current Subscription Total</div>
                                        <div className="mt-1" style={{ color: "#0f172a", fontWeight: 700 }}>
                                          ${existingPrice}/month
                                        </div>
                                      </div>
                                      {(subscriptionIncludedHomes > 0 || subscriptionIncludedResidents > 0) && (
                                        <div
                                          className="mt-3"
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.75rem",
                                            flexWrap: "wrap",
                                            background: "#ffffff",
                                            border: "1px solid #dbeafe",
                                            borderRadius: "14px",
                                            padding: "0.8rem 0.9rem",
                                          }}
                                        >
                                          <div style={{ fontSize: "0.78rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>
                                            Subscription Capacity
                                          </div>
                                          <Tag value={`${subscriptionIncludedHomes} Home${subscriptionIncludedHomes === 1 ? "" : "s"}`} severity="info" rounded />
                                          <Tag value={`${subscriptionIncludedResidents} Resident${subscriptionIncludedResidents === 1 ? "" : "s"}`} severity="warning" rounded />
                                        </div>
                                      )}
                                      {cancelAtPeriodEnd && subscriptionCancelDate && (
                                        <div className="mt-3" style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "12px", padding: "0.85rem 1rem", color: "#185DA2" }}>
                                          Subscription will cancel on {subscriptionCancelDate.toLocaleDateString()} unless you resume it before then.
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <></>
                          )}
                        </div>
                      )}
                      visibleSubscribedSummaryServices={visibleSubscribedSummaryServices}
                      sectionCardStyle={sectionCardStyle}
                      cardTitleTemplate={cardTitleTemplate}
                      renderCompactPlanRow={renderCompactPlanRow}
                      selectedSubscriptionServices={selectedSubscriptionServices}
                      selectedSubscriptionServiceTypes={selectedSubscriptionServiceTypes}
                      renderSubscriptionSignupCard={renderSubscriptionSignupCard}
                      useExpandedPlanBuilder={useProviderPlanBuilder}
                      renderPlanSectionWithoutCard={useProviderPlanBuilder || isNurseBillingRole}
                      renderSummaryBesidePlan
                      totalPrice={totalPrice}
                      paymentIntentRef={paymentIntentRef}
                      existingPrice={existingPrice}
                      cartLineItems={cartLineItems}
                      newItemsSubtotal={newItemsSubtotal}
                      taxDetails={taxDetails}
                      taxAmount={taxAmount}
                      totalWithTax={totalWithTax}
                      isSubscriptionUpdatePreview={isUserSubscribed}
                      existingPriceLabel={existingPriceLabel}
                      updatedRecurringPrice={updatedRecurringPrice}
                      updatedRecurringPriceLabel={updatedRecurringPriceLabel}
                      newItemsSubtotalLabel={newItemsSubtotalLabel}
                      totalPriceLabel={totalPriceLabel}
                      subscriptionCartHelperText={subscriptionCartHelperText}
                      trialEndDate={trialEndDate}
                      subscriptionCancelDate={subscriptionCancelDate}
                      onProceedToPaymentSubmit={onProceedToPaymentSubmit}
                      loadingPaymentIntent={loadingPaymentIntent}
                      paymentMethodSummary={paymentMethodSummary}
                      renderPaymentSection={renderPaymentSection}
                      paymentMode={paymentMode}
                      billingTabIndex={billingTabIndex}
                    />
                  ) : (
                    <>
                      {!isUserSubscribed || billingTabIndex === 0 ? (
                        <BillingSubscriptionTabContent
                          isUserSubscribed={isUserSubscribed}
                          renderPaymentAttentionBanner={renderPaymentAttentionBanner}
                          renderOutstandingPaymentsCard={renderOutstandingPaymentsCard}
                          paymentMethodSummary={paymentMethodSummary}
                          subscriptionSummaryContent={(
                            <div>
                              {isUserSubscribed ? (
                                <>
                                  {isNurseBillingRole ? (
                                    <>
                                      {cancelAtPeriodEnd && subscriptionCancelDate && (
                                        <div className="mt-1" style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "12px", padding: "0.85rem 1rem", color: "#185DA2" }}>
                                          Subscription will cancel on {subscriptionCancelDate.toLocaleDateString()} unless you resume it before then.
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className="grid align-items-start">
                                      <div className="col-12">
                                        <div
                                          style={{
                                            background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 65%)",
                                            borderRadius: "18px",
                                            border: "1px solid #bfdbfe",
                                            padding: "1.1rem 1.15rem",
                                          }}
                                        >
                                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap" }}>
                                            <div>
                                              <div style={{ color: "#185DA2", fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>
                                                Subscription
                                              </div>
                                            </div>
                                          </div>
                                          <div
                                            className="mt-3"
                                            style={{
                                              background: "#ffffff",
                                              border: "1px solid #dbeafe",
                                              borderRadius: "14px",
                                              padding: "0.85rem 0.95rem",
                                            }}
                                          >
                                            <div style={{ color: "#64748b", fontSize: "0.82rem" }}>Current Subscription Total</div>
                                            <div className="mt-1" style={{ color: "#0f172a", fontWeight: 700 }}>
                                              ${existingPrice}/month
                                            </div>
                                          </div>
                                          {(subscriptionIncludedHomes > 0 || subscriptionIncludedResidents > 0) && (
                                            <div
                                              className="mt-3"
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "0.75rem",
                                                flexWrap: "wrap",
                                                background: "#ffffff",
                                                border: "1px solid #dbeafe",
                                                borderRadius: "14px",
                                                padding: "0.8rem 0.9rem",
                                              }}
                                            >
                                              <div style={{ fontSize: "0.78rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>
                                                Subscription Capacity
                                              </div>
                                              <Tag value={`${subscriptionIncludedHomes} Home${subscriptionIncludedHomes === 1 ? "" : "s"}`} severity="info" rounded />
                                              <Tag value={`${subscriptionIncludedResidents} Resident${subscriptionIncludedResidents === 1 ? "" : "s"}`} severity="warning" rounded />
                                            </div>
                                          )}
                                          {cancelAtPeriodEnd && subscriptionCancelDate && (
                                            <div className="mt-3" style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "12px", padding: "0.85rem 1rem", color: "#185DA2" }}>
                                              Subscription will cancel on {subscriptionCancelDate.toLocaleDateString()} unless you resume it before then.
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <></>
                              )}
                            </div>
                          )}
                          visibleSubscribedSummaryServices={visibleSubscribedSummaryServices}
                          sectionCardStyle={sectionCardStyle}
                          cardTitleTemplate={cardTitleTemplate}
                          renderCompactPlanRow={renderCompactPlanRow}
                          selectedSubscriptionServices={selectedSubscriptionServices}
                          selectedSubscriptionServiceTypes={selectedSubscriptionServiceTypes}
                          renderSubscriptionSignupCard={renderSubscriptionSignupCard}
                          useExpandedPlanBuilder={useProviderPlanBuilder}
                          renderPlanSectionWithoutCard={useProviderPlanBuilder || isNurseBillingRole}
                          renderSummaryBesidePlan
                          totalPrice={totalPrice}
                          paymentIntentRef={paymentIntentRef}
                          existingPrice={existingPrice}
                          cartLineItems={cartLineItems}
                          newItemsSubtotal={newItemsSubtotal}
                          taxDetails={taxDetails}
                          taxAmount={taxAmount}
                          totalWithTax={totalWithTax}
                          isSubscriptionUpdatePreview={isUserSubscribed}
                          existingPriceLabel={existingPriceLabel}
                          updatedRecurringPrice={updatedRecurringPrice}
                          updatedRecurringPriceLabel={updatedRecurringPriceLabel}
                          newItemsSubtotalLabel={newItemsSubtotalLabel}
                          totalPriceLabel={totalPriceLabel}
                          subscriptionCartHelperText={subscriptionCartHelperText}
                          trialEndDate={trialEndDate}
                          subscriptionCancelDate={subscriptionCancelDate}
                          onProceedToPaymentSubmit={onProceedToPaymentSubmit}
                          loadingPaymentIntent={loadingPaymentIntent}
                          renderPaymentSection={renderPaymentSection}
                          paymentMode={paymentMode}
                          billingTabIndex={billingTabIndex}
                        />
                      ) : (
                        <BillingOneTimeTabContent
                          automationSummaryContent={automationSummaryContent}
                          automationCreditUsageContent={automationCreditUsageContent}
                          renderBundlePackages={renderBundlePackages}
                          paymentMode={paymentMode}
                          billingTabIndex={billingTabIndex}
                          renderPaymentSection={renderPaymentSection}
                        />
                      )}
                    </>
                  )}
                </div>
                <BillingOneTimeCheckoutDialog
                  header={oneTimeCheckoutDetails?.title || "Complete Purchase"}
                  visible={oneTimeCheckoutVisible && !!setUpIntentClientSecret && isOneTimeCheckoutMode}
                  onHide={closeOneTimeCheckoutDialog}
                  details={oneTimeCheckoutDetails}
                  renderCheckoutFormContent={renderCheckoutFormContent}
                />
                <BillingPlanComparisonDialog
                  config={planComparisonConfig}
                  visible={planComparisonVisible}
                  onHide={() => setPlanComparisonVisible(false)}
                />
                <BillingPaymentHistoryDialog
                  visible={paymentHistoryVisible}
                  onHide={() => setPaymentHistoryVisible(false)}
                  sectionCardStyle={sectionCardStyle}
                  cardTitleTemplate={cardTitleTemplate}
                  invoiceHistory={invoiceHistory}
                  getInvoiceStatusMeta={getInvoiceStatusMeta}
                  formatHistoryDate={formatHistoryDate}
                  openExternalUrl={openExternalUrl}
                  nextInvoiceCursor={nextInvoiceCursor}
                  onLoadMoreInvoices={onLoadMoreInvoices}
                  loadingMoreInvoices={loadingMoreInvoices}
                  featureCreditPurchaseHistory={featureCreditPurchaseHistory}
                  getOneTimePurchaseStatusMeta={getOneTimePurchaseStatusMeta}
                  getFeatureCreditOption={getFeatureCreditOption}
                  nextFeatureCursor={nextFeatureCursor}
                  onLoadMoreFeaturePurchases={onLoadMoreFeaturePurchases}
                  loadingMoreFeatureHistory={loadingMoreFeatureHistory}
                />
                <BillingPaymentDetailsDialog
                  visible={paymentDetailsVisible}
                  onHide={() => setPaymentDetailsVisible(false)}
                  paymentMethodSummary={paymentMethodSummary}
                  onUpdatePaymentMethod={() => onUpdateCardSubmit(billingTabIndex > 0 ? billingTabIndex : 0)}
                  loading={loadingPaymentIntent && paymentMode === "cardUpdate"}
                />
                <BillingAddressDialog
                  visible={billingAddressDialogVisible}
                  billingAddressSaving={billingAddressSaving}
                  onHide={() => {
                    if (billingAddressSaving) {
                      return;
                    }
                    setSubscriptionWizardDialogVisible(false);
                    setBillingAddressDialogVisible(false);
                    pendingBillingAddressActionRef.current = null;
                    setBillingAddressErrors({});
                  }}
                  appDialogContentStyle={appDialogContentStyle}
                  appDialogHeaderStyle={appDialogHeaderStyle}
                  appDialogFooterStyle={appDialogFooterStyle}
                  paymentMethodSummary={pendingBillingAddressActionRef.current ? null : paymentMethodSummary}
                  wizardStep={subscriptionWizardStep}
                  paymentContent={setUpIntentClientSecret ? (
                    <div style={{ display: "grid", gap: "1rem" }}>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>Payment Details</div>
                      {renderCheckoutFormContent("Start Subscription")}
                    </div>
                  ) : null}
                  onWizardStepChange={setSubscriptionWizardStep}
                  showSaveBillingAddress={!pendingBillingAddressActionRef.current && !subscriptionWizardDialogVisible}
                  showPaymentMethodAction={Boolean(pendingBillingAddressActionRef.current) || subscriptionWizardDialogVisible}
                  onCancel={() => {
                    setSubscriptionWizardDialogVisible(false);
                    setSetUpIntentClientSecret("");
                    setPaymentIntentType("");
                    setSubscriptionWizardStep(0);
                    setBillingAddressDialogVisible(false);
                    pendingBillingAddressActionRef.current = null;
                    setBillingAddressErrors({});
                  }}
                  onSaveBillingAddress={onSaveBillingAddress}
                  onSaveBillingAddressAndUpdatePaymentMethod={onSaveBillingAddressAndUpdatePaymentMethod}
                  getBillingFieldError={getBillingFieldError}
                  billingAddressDraft={billingAddressDraft}
                  onBillingAddressElementChange={onBillingAddressElementChange}
                  hasCompleteBillingAddress={hasCompleteBillingAddress}
                />
              </>)
          }
        </Card >
      </div>
    </div >
  );
}

export default BillingPage;






